import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Apple,
  ArrowLeft,
  BookOpen,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Dumbbell,
  FileText,
  ImageOff,
  LayoutGrid,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  Settings as SettingsIcon,
  Trash2,
  UploadCloud,
  UtensilsCrossed,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { useCompanyDetails } from '@/hooks/useCompanyDetails'
import { apiService } from '@/services/api'
import NutritionProgramsWorkspace from './NutritionProgramsWorkspace'
import ExercisePlanTemplatesWorkspace from './ExercisePlanTemplatesWorkspace'
import UserUpload from './UserUpload'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn, formatNumberSmart } from '@/lib/utils'

const ConfirmDeleteModal = ({ open, onOpenChange, onConfirm, title, description, isDeleting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[440px]">
      <DialogHeader>
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <Trash2 className="h-6 w-6 text-red-600" />
        </div>
        <DialogTitle className="text-center text-xl">{title || 'Confirm Deletion'}</DialogTitle>
        <DialogDescription className="text-center text-slate-500">
          {description || 'Are you sure you want to delete this item? This action cannot be undone.'}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center sm:space-x-4">
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDeleting} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button variant="destructive" onClick={onConfirm} disabled={isDeleting} className="w-full sm:w-auto">
          {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
)

const FOOD_DEFAULT_FORM = {
  id: null,
  name: '',
  description: '',
  note: '',
  video_url: '',
  qty: '',
  unit: '',
  calories: '',
  protein: '',
  carbs: '',
  fat: '',
  sugars: '',
  fiber: '',
  calcium: '',
  iron: '',
  magnesium: '',
  phosphorus: '',
  potassium: '',
  sodium: '',
  zinc: '',
  vitamin_a: '',
  vitamin_e: '',
  vitamin_d: '',
  vitamin_d3: '',
  vitamin_c: '',
  thiamin: '',
  riboflavin: '',
  niacin: '',
  pantothenic_acid: '',
  vitamin_b6: '',
  folate: '',
  vitamin_b12: '',
  leucine: '',
  cholesterol: '',
  fatty_acids: '',
  dha: '',
  epa: '',
  ala: '',
  foodtemplatecategory_id: '',
  newCategoryName: '',
  newCategoryParentId: '',
  imageFiles: [],
  recipeFile: null,
}

const EXERCISE_DEFAULT_FORM = {
  id: null,
  name: '',
  description: '',
  note: '',
  video_url: '',
  exercisetemplatecategory_id: '',
  newCategoryName: '',
  newCategoryParentId: '',
  imageFiles: [],
}

const MEAL_DEFAULT_FORM = {
  id: null,
  name: '',
  description: '',
  note: '',
  video_url: '',
  mealtemplatecategory_id: '',
  newCategoryName: '',
  newCategoryParentId: '',
  mealFoods: [],
}

const RECIPE_DEFAULT_FORM = {
  id: null,
  name: '',
  description: '',
  instructions: '',
  note: '',
  video_url: '',
  recipetemplatecategory_id: '',
  newCategoryName: '',
  newCategoryParentId: '',
  unit: '',
  qty: '',
  recipeFoods: [],
  imageFiles: [],
  useCopiedRecipeImages: false,
}

const FOOD_MACRO_FIELDS = [
  { key: 'calories', label: 'Calories' },
  { key: 'protein', label: 'Protein' },
  { key: 'carbs', label: 'Carbs' },
  { key: 'fat', label: 'Fat' },
]

const FOOD_MICRO_FIELDS = [
  { key: 'sugars', label: 'Sugars' },
  { key: 'fiber', label: 'Fiber' },
  { key: 'calcium', label: 'Calcium' },
  { key: 'iron', label: 'Iron' },
  { key: 'magnesium', label: 'Magnesium' },
  { key: 'phosphorus', label: 'Phosphorus' },
  { key: 'potassium', label: 'Potassium' },
  { key: 'sodium', label: 'Sodium' },
  { key: 'zinc', label: 'Zinc' },
  { key: 'vitamin_a', label: 'Vitamin A' },
  { key: 'vitamin_e', label: 'Vitamin E' },
  { key: 'vitamin_d', label: 'Vitamin D' },
  { key: 'vitamin_d3', label: 'Vitamin D3' },
  { key: 'vitamin_c', label: 'Vitamin C' },
  { key: 'thiamin', label: 'Thiamin' },
  { key: 'riboflavin', label: 'Riboflavin' },
  { key: 'niacin', label: 'Niacin' },
  { key: 'pantothenic_acid', label: 'Pantothenic Acid' },
  { key: 'vitamin_b6', label: 'Vitamin B6' },
  { key: 'folate', label: 'Folate' },
  { key: 'vitamin_b12', label: 'Vitamin B12' },
  { key: 'leucine', label: 'Leucine' },
  { key: 'cholesterol', label: 'Cholesterol' },
  { key: 'fatty_acids', label: 'Fatty Acids' },
  { key: 'dha', label: 'DHA' },
  { key: 'epa', label: 'EPA' },
  { key: 'ala', label: 'ALA' },
]

const FOOD_MICRO_GROUPS = [
  {
    label: 'Fiber & Minerals',
    keys: ['sugars', 'fiber', 'calcium', 'iron', 'magnesium', 'phosphorus', 'potassium', 'sodium', 'zinc'],
  },
  {
    label: 'Vitamins',
    keys: ['vitamin_a', 'vitamin_e', 'vitamin_d', 'vitamin_d3', 'vitamin_c', 'thiamin', 'riboflavin', 'niacin', 'pantothenic_acid', 'vitamin_b6', 'folate', 'vitamin_b12'],
  },
  {
    label: 'Fatty Acids & Other',
    keys: ['leucine', 'cholesterol', 'fatty_acids', 'dha', 'epa', 'ala'],
  },
]

const FOOD_TABS = [
  { id: 'create', label: 'Create', icon: Plus },
  { id: 'edit', label: 'Edit', icon: Pencil },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
]

const EXERCISE_TABS = [
  { id: 'create', label: 'Create', icon: Plus },
  { id: 'edit', label: 'Edit', icon: Pencil },
  { id: 'categories', label: 'Categories', icon: SettingsIcon },
]

const MEAL_TABS = [
  { id: 'create', label: 'Create', icon: Plus },
  { id: 'edit', label: 'Edit', icon: Pencil },
  { id: 'categories', label: 'Categories', icon: SettingsIcon },
]

const RECIPE_TABS = [
  { id: 'create', label: 'Create', icon: Plus },
  { id: 'edit', label: 'Edit', icon: Pencil },
  { id: 'categories', label: 'Categories', icon: SettingsIcon },
]

const WORKSPACES = [
  {
    id: 'food-templates',
    title: 'Food Templates',
    description: 'Create, edit, and organize your food template library.',
    icon: Apple,
  },
  {
    id: 'exercise-templates',
    title: 'Exercise Templates',
    description: 'Manage exercise templates, category assignments, and media.',
    icon: Dumbbell,
  },
  {
    id: 'meal-templates',
    title: 'Meal Templates',
    description: 'Build and manage meal templates, food assignments, and category structure.',
    icon: UtensilsCrossed,
  },
  {
    id: 'recipe-templates',
    title: 'Recipe Templates',
    description: 'Build and review recipe templates, ingredient libraries, and category structure.',
    icon: BookOpen,
  },
  {
    id: 'nutrition-programs',
    title: 'Nutrition Program Templates',
    description: 'Nutrition program template management will be added next.',
    icon: ClipboardList,
  },
  {
    id: 'exercise-plans',
    title: 'Exercise Plan Templates',
    description: 'Exercise plan template management will be added next.',
    icon: FileText,
  },
  {
    id: 'bulk-user-upload',
    title: 'Bulk User Upload',
    description: 'Upload users in bulk using the same spreadsheet workflow as the legacy page.',
    icon: UploadCloud,
  },
]

const EMPTY_EDIT_PAGINATION = { pageIndex: 0, pageSize: 25 }
const EMPTY_SEARCH_PAGINATION = { pageIndex: 0, pageSize: 10 }

const toFormValue = (value) => {
  if (value === null || value === undefined) return ''
  return `${value}`
}

const parseNumberField = (value) => {
  if (value === '' || value === null || value === undefined) return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const truncateText = (value, limit = 140) => {
  if (!value) return 'Empty'
  if (value.length <= limit) return value
  return `${value.slice(0, limit)}...`
}

const extractId = (payload) => payload?.data?.id || payload?.success?.id || payload?.id || null

const getCategoryName = (categories, categoryId) => {
  if (!categoryId) return ''
  const match = categories.find((category) => `${category.id}` === `${categoryId}`)
  return match?.name || ''
}

const normalizeFoodTemplate = (template) => {
  const data = template?.data || template || {}
  return {
    ...FOOD_DEFAULT_FORM,
    id: data.id || null,
    name: data.name || '',
    description: data.description || '',
    note: data.note || '',
    video_url: data.video_url || '',
    qty: toFormValue(data.qty),
    unit: data.unit || '',
    calories: toFormValue(data.calories),
    protein: toFormValue(data.protein),
    carbs: toFormValue(data.carbs),
    fat: toFormValue(data.fat),
    sugars: toFormValue(data.sugars),
    fiber: toFormValue(data.fiber),
    calcium: toFormValue(data.calcium),
    iron: toFormValue(data.iron),
    magnesium: toFormValue(data.magnesium),
    phosphorus: toFormValue(data.phosphorus),
    potassium: toFormValue(data.potassium),
    sodium: toFormValue(data.sodium),
    zinc: toFormValue(data.zinc),
    vitamin_a: toFormValue(data.vitamin_a),
    vitamin_e: toFormValue(data.vitamin_e),
    vitamin_d: toFormValue(data.vitamin_d),
    vitamin_d3: toFormValue(data.vitamin_d3),
    vitamin_c: toFormValue(data.vitamin_c),
    thiamin: toFormValue(data.thiamin),
    riboflavin: toFormValue(data.riboflavin),
    niacin: toFormValue(data.niacin),
    pantothenic_acid: toFormValue(data.pantothenic_acid),
    vitamin_b6: toFormValue(data.vitamin_b6),
    folate: toFormValue(data.folate),
    vitamin_b12: toFormValue(data.vitamin_b12),
    leucine: toFormValue(data.leucine),
    cholesterol: toFormValue(data.cholesterol),
    fatty_acids: toFormValue(data.fatty_acids),
    dha: toFormValue(data.dha),
    epa: toFormValue(data.epa),
    ala: toFormValue(data.ala),
    foodtemplatecategory_id: toFormValue(
      data.foodtemplatecategory_id || data.foodtemplatecategory?.id || ''
    ),
    imageFiles: [],
    recipeFile: null,
  }
}

const findExerciseCategoryByName = (categories, categoryName) => {
  if (!categoryName) return null
  return categories.find((category) => category.name?.toLowerCase() === `${categoryName}`.toLowerCase()) || null
}

const resolveTopLevelExerciseCategoryId = (tree, categoryId) => {
  if (!categoryId) return ''

  const visit = (nodes, topLevelId = null) => {
    for (const node of nodes || []) {
      const nextTopLevelId = topLevelId ?? node.id
      if (`${node.id}` === `${categoryId}`) {
        return nextTopLevelId
      }

      if (Array.isArray(node.children) && node.children.length) {
        const match = visit(node.children, nextTopLevelId)
        if (match) return match
      }
    }

    return ''
  }

  return toFormValue(visit(tree))
}

const normalizeExerciseTemplate = (template, categories = [], categoryTree = []) => {
  const data = template?.data || template || {}
  const rawCategoryId = data.exercisetemplatecategory_id || data.exercisetemplatecategory?.id || ''
  const fallbackCategory =
    findExerciseCategoryByName(categories, data.exercisetemplatecategory?.name) ||
    findExerciseCategoryByName(categories, data.category) ||
    findExerciseCategoryByName(categories, data.cat_name)
  const resolvedCategoryId =
    resolveTopLevelExerciseCategoryId(categoryTree, rawCategoryId) ||
    toFormValue(fallbackCategory?.id || '')

  return {
    ...EXERCISE_DEFAULT_FORM,
    id: data.id || null,
    name: data.name || '',
    description: data.description || '',
    note: data.note || '',
    video_url: data.video_url || '',
    exercisetemplatecategory_id: resolvedCategoryId,
    imageFiles: [],
  }
}

const createDraftRowId = (prefix = 'draft') =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

const normalizeMealTemplate = (template) => ({
  ...MEAL_DEFAULT_FORM,
  id: template?.id || null,
  name: template?.name || '',
  description: template?.description || '',
  note: template?.note || '',
  video_url: template?.video_url || '',
  mealtemplatecategory_id: toFormValue(
    template?.mealtemplatecategory_id || template?.mealtemplatecategory?.id || ''
  ),
  mealFoods: [],
})

const normalizeRecipeTemplate = (recipe) => {
  const primaryCategory = Array.isArray(recipe?.recipecategories) ? recipe.recipecategories[0] : null

  return {
    ...RECIPE_DEFAULT_FORM,
    id: recipe?.id || null,
    name: recipe?.name || '',
    description: recipe?.description || '',
    instructions: recipe?.instructions || '',
    note: recipe?.note || '',
    video_url: recipe?.video_url || '',
    recipetemplatecategory_id: toFormValue(primaryCategory?.id || ''),
    unit: recipe?.unit || '',
    qty: toFormValue(recipe?.qty || ''),
  }
}

const toPositiveNumber = (value, fallback = 0) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return parsed
}

const normalizeNutritionCalories = (item) => {
  const directCalories = item?.calories
  if (directCalories !== '' && directCalories !== null && directCalories !== undefined) {
    return parseNumberField(directCalories)
  }

  const nutrientValue = item?.nutrient_value
  if (nutrientValue !== '' && nutrientValue !== null && nutrientValue !== undefined) {
    return parseNumberField(nutrientValue)
  }

  return 0
}

const buildMealFoodDraft = (item, sourceType = 'food-template', overrides = {}) => {
  const parsedBaseQty = toPositiveNumber(overrides.baseQty ?? item?.qty ?? item?.serving_qty ?? 1, 1)
  const baseQty = parsedBaseQty > 0 ? parsedBaseQty : 1
  const category =
    overrides.category ||
    item?.cat_name ||
    item?.foodtemplatecategory?.name ||
    item?.category ||
    item?.brand_name ||
    item?.nix_brand_name ||
    ''

  return {
    id: createDraftRowId(sourceType),
    sourceType,
    sourceId:
      overrides.sourceId ??
      item?.id ??
      item?.resource_id ??
      item?.nix_item_id ??
      item?.ndb_no ??
      null,
    name: overrides.name || item?.name || item?.food_name || item?.item_name || 'Food',
    category,
    unit: overrides.unit || item?.unit || item?.serving_unit || item?.serving_uom || '',
    qty: overrides.qty ?? baseQty,
    baseQty,
    baseCalories: parseNumberField(overrides.baseCalories ?? item?.calories ?? item?.nf_calories),
    baseProtein: parseNumberField(overrides.baseProtein ?? item?.protein ?? item?.nf_protein),
    baseCarbs: parseNumberField(overrides.baseCarbs ?? item?.carbs ?? item?.nf_total_carbohydrate),
    baseFat: parseNumberField(overrides.baseFat ?? item?.fat ?? item?.nf_total_fat),
    thumbnail: item?.thumbnail || item?.photo?.thumb || '',
    detail: overrides.detail || item || null,
    detailRows: overrides.detailRows || null,
    servingLabel: overrides.servingLabel || '',
  }
}

const buildNutritionSearchDraft = (item) => ({
  id: item?.resource_id || item?.nix_item_id || item?.food_name || item?.name || createDraftRowId('nutrition-result'),
  name: item?.item_name || item?.name || 'Food',
  brandName: item?.brand_name || '',
  unit: item?.serving_uom || '',
  servingQty: parseNumberField(item?.serving_qty),
  calories: normalizeNutritionCalories(item),
  thumbnail: item?.thumbnail || '',
  raw: item,
})

const getMealFoodScale = (food) => {
  const qty = Math.max(parseNumberField(food?.qty), 0)
  const baseQty = Math.max(parseNumberField(food?.baseQty), 0)
  if (qty === 0) return 0
  if (baseQty > 0) return qty / baseQty
  return qty
}

const getScaledMealFoodMacros = (food) => {
  const scale = getMealFoodScale(food)
  return {
    calories: parseNumberField(food?.baseCalories) * scale,
    protein: parseNumberField(food?.baseProtein) * scale,
    carbs: parseNumberField(food?.baseCarbs) * scale,
    fat: parseNumberField(food?.baseFat) * scale,
  }
}

const calculateMealMacroTotals = (foods = []) =>
  foods.reduce(
    (totals, food) => {
      const macros = getScaledMealFoodMacros(food)
      totals.calories += macros.calories
      totals.protein += macros.protein
      totals.carbs += macros.carbs
      totals.fat += macros.fat
      return totals
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  )

const buildNutritionDetailMap = (detail) => {
  const fields = [
    ['brand_name', 'Brand'],
    ['calories', 'Calories'],
    ['protein', 'Protein'],
    ['carbs', 'Carbs'],
    ['fat', 'Fat'],
    ['fiber', 'Fiber'],
    ['sugars', 'Sugars'],
    ['serving_qty', 'Serving Qty'],
    ['serving_unit', 'Serving Unit'],
    ['serving_weight_grams', 'Serving Weight (g)'],
    ['cholesterol', 'Cholesterol'],
    ['sodium', 'Sodium'],
    ['potassium', 'Potassium'],
    ['calcium', 'Calcium'],
    ['iron', 'Iron'],
    ['vitamin_c', 'Vitamin C'],
  ]

  return fields
    .map(([key, label]) => ({
      key,
      label,
      value: detail?.[key],
    }))
    .filter((field) => field.value !== null && field.value !== undefined && field.value !== '')
}

const getNutritionMeasureOptions = (detail) => {
  const altMeasures = Array.isArray(detail?.alt_measures) ? detail.alt_measures : []
  const primaryMeasure = {
    id: `${detail?.food_name || detail?.name || 'measure'}-primary`,
    qty: parseNumberField(detail?.serving_qty || detail?.qty || 1),
    measure: detail?.serving_unit || detail?.unit || 'serving',
    serving_weight: parseNumberField(detail?.serving_weight_grams),
    calories: parseNumberField(detail?.calories ?? detail?.nf_calories),
    protein: parseNumberField(detail?.protein ?? detail?.nf_protein),
    carbs: parseNumberField(detail?.carbs ?? detail?.nf_total_carbohydrate),
    fat: parseNumberField(detail?.fat ?? detail?.nf_total_fat),
  }

  const mappedAltMeasures = altMeasures.map((measure, index) => ({
    id: `${detail?.food_name || detail?.name || 'measure'}-${measure?.seq ?? index}`,
    qty: parseNumberField(measure?.qty || 1),
    measure: measure?.measure || detail?.serving_unit || 'serving',
    serving_weight: parseNumberField(measure?.serving_weight),
    calories: parseNumberField(measure?.calories),
    protein: parseNumberField(measure?.protein),
    carbs: parseNumberField(measure?.carbs),
    fat: parseNumberField(measure?.fat),
    raw: measure,
  }))

  return [primaryMeasure, ...mappedAltMeasures].filter(
    (measure, index, arr) =>
      arr.findIndex(
        (candidate) =>
          `${candidate.qty}-${candidate.measure}`.toLowerCase() ===
          `${measure.qty}-${measure.measure}`.toLowerCase()
      ) === index
  )
}

const buildMealFoodFromNutritionMeasure = (detail, measure) =>
  buildMealFoodDraft(detail, 'nutrition', {
    name: detail?.food_name || detail?.name || 'Food',
    unit: measure?.measure || detail?.serving_unit || detail?.unit || '',
    qty: measure?.qty || 1,
    baseQty: measure?.qty || 1,
    baseCalories: measure?.calories ?? detail?.calories ?? detail?.nf_calories,
    baseProtein: measure?.protein ?? detail?.protein ?? detail?.nf_protein,
    baseCarbs: measure?.carbs ?? detail?.carbs ?? detail?.nf_total_carbohydrate,
    baseFat: measure?.fat ?? detail?.fat ?? detail?.nf_total_fat,
    category: detail?.brand_name || detail?.nix_brand_name || '',
    detail,
    detailRows: buildNutritionDetailMap(detail),
    servingLabel: `${formatNumberSmart(measure?.qty || 1)} ${measure?.measure || detail?.serving_unit || detail?.unit || ''}`.trim(),
  })

const buildRecipeFoodDraftFromRecipeDetail = (food) =>
  buildMealFoodDraft(food, 'recipe-food', {
    sourceId: food?.id ?? null,
    name: food?.name || 'Food',
    category: food?.cat_name || '',
    unit: food?.unit || '',
    qty: food?.qty ?? 1,
    baseQty: food?.qty ?? 1,
    baseCalories: food?.calories,
    baseProtein: food?.protein,
    baseCarbs: food?.carbs,
    baseFat: food?.fat,
    detail: food,
    servingLabel: `${formatNumberSmart(food?.qty ?? 1)} ${food?.unit || ''}`.trim(),
  })

const buildMealFoodCreatePayload = (food) => {
  const detail = food?.detail && typeof food.detail === 'object' ? { ...food.detail } : {}
  const quantity = Math.max(parseNumberField(food?.qty || 1), 0)
  const payload = {
    ...detail,
    name: food?.name || detail?.name || detail?.food_name || detail?.item_name || 'Food',
    quantity,
  }

  if (payload.qty === undefined || payload.qty === null || payload.qty === '') {
    payload.qty = Math.max(parseNumberField(food?.baseQty || quantity || 1), 0)
  }

  if (!payload.unit) {
    payload.unit = food?.unit || detail?.measure || detail?.serving_unit || ''
  }

  ;[
    ['calories', food?.baseCalories],
    ['protein', food?.baseProtein],
    ['carbs', food?.baseCarbs],
    ['fat', food?.baseFat],
  ].forEach(([key, fallback]) => {
    if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
      payload[key] = parseNumberField(fallback)
    }
  })

  return payload
}

const buildMealTemplateAddFoodPayloadFromTemplate = (row) =>
  buildMealFoodCreatePayload({
    ...buildMealFoodDraft(row, 'food-template', { detail: row }),
    detail: row,
  })

const buildMealTemplateAddFoodPayloadFromNutrition = (detail, measure, selectedIndex = 0) => {
  const unit = measure?.measure || detail?.serving_unit || detail?.unit || 'serving'
  const qty = Math.max(parseNumberField(measure?.qty || detail?.serving_qty || detail?.qty || 1), 0)
  const rawMeasure = measure?.raw && typeof measure.raw === 'object' ? { ...measure.raw } : {}

  return {
    ...rawMeasure,
    name: `${formatNumberSmart(qty)} ${unit}`.trim(),
    item_name: detail?.food_name || detail?.name || detail?.item_name || 'Food',
    unit,
    measure: rawMeasure.measure || unit,
    qty,
    selected: true,
    selectedIndex,
    serving_weight:
      rawMeasure.serving_weight ??
      parseNumberField(measure?.serving_weight ?? detail?.serving_weight_grams),
    calories: parseNumberField(measure?.calories ?? detail?.calories ?? detail?.nf_calories),
    protein: parseNumberField(measure?.protein ?? detail?.protein ?? detail?.nf_protein),
    carbs: parseNumberField(measure?.carbs ?? detail?.carbs ?? detail?.nf_total_carbohydrate),
    fat: parseNumberField(measure?.fat ?? detail?.fat ?? detail?.nf_total_fat),
    sugars: parseNumberField(detail?.sugars ?? detail?.nf_sugars),
    fiber: parseNumberField(detail?.fiber ?? detail?.nf_dietary_fiber),
    calcium: parseNumberField(detail?.calcium ?? detail?.nf_calcium),
    iron: parseNumberField(detail?.iron ?? detail?.nf_iron),
    magnesium: parseNumberField(detail?.magnesium ?? detail?.nf_magnesium),
    phosphorus: parseNumberField(detail?.phosphorus ?? detail?.nf_p),
    potassium: parseNumberField(detail?.potassium ?? detail?.nf_potassium),
    sodium: parseNumberField(detail?.sodium ?? detail?.nf_sodium),
    zinc: parseNumberField(detail?.zinc ?? detail?.nf_zinc),
    vitamin_a: parseNumberField(detail?.vitamin_a ?? detail?.nf_vitamin_a_dv),
    vitamin_e: parseNumberField(detail?.vitamin_e ?? detail?.nf_vitamin_e),
    vitamin_d: parseNumberField(detail?.vitamin_d ?? detail?.nf_vitamin_d),
    vitamin_c: parseNumberField(detail?.vitamin_c ?? detail?.nf_vitamin_c),
    thiamin: parseNumberField(detail?.thiamin ?? detail?.nf_thiamin),
    riboflavin: parseNumberField(detail?.riboflavin ?? detail?.nf_riboflavin),
    niacin: parseNumberField(detail?.niacin ?? detail?.nf_niacin),
    pantothenic_acid: parseNumberField(detail?.pantothenic_acid ?? detail?.nf_pantothenic_acid),
    vitamin_b6: parseNumberField(detail?.vitamin_b6 ?? detail?.nf_vitamin_b6),
    folate: parseNumberField(detail?.folate ?? detail?.nf_folate),
    vitamin_b12: parseNumberField(detail?.vitamin_b12 ?? detail?.nf_vitamin_b12),
    leucine: parseNumberField(detail?.leucine ?? detail?.nf_leucine),
    cholesterol: parseNumberField(detail?.cholesterol ?? detail?.nf_cholesterol),
    fatty_acids: parseNumberField(detail?.fatty_acids ?? detail?.nf_fatty_acids ?? detail?.nf_saturated_fat),
    dha: parseNumberField(detail?.dha ?? detail?.nf_dha),
    epa: parseNumberField(detail?.epa ?? detail?.nf_epa),
    ala: parseNumberField(detail?.ala ?? detail?.nf_ala),
    brand_name: detail?.brand_name || detail?.nix_brand_name || '',
    thumbnail: detail?.photo?.thumb || detail?.thumbnail || '',
  }
}

const buildMealCreatePayload = (form) => ({
  name: form.name.trim(),
  video_url: form.video_url.trim(),
  note: form.note.trim(),
  description: form.description.trim(),
  mealcategory: form.mealtemplatecategory_id
    ? Number(form.mealtemplatecategory_id)
    : form.newCategoryName.trim(),
  foods: (form.mealFoods || []).map(buildMealFoodCreatePayload),
})

const buildMealUpdatePayload = (form) => ({
  name: form.name.trim(),
  video_url: form.video_url.trim(),
  note: form.note.trim(),
  description: form.description.trim(),
  mealcategory: form.mealtemplatecategory_id ? Number(form.mealtemplatecategory_id) : '',
})

const buildRecipeFoodCreatePayload = (food) => {
  const detail = food?.detail && typeof food.detail === 'object' ? { ...food.detail } : {}
  const quantity = Math.max(parseNumberField(food?.qty || 1), 0)
  const payload = {
    ...detail,
    name: food?.name || detail?.name || detail?.food_name || detail?.item_name || 'Food',
    unit: food?.unit || detail?.unit || detail?.measure || detail?.serving_unit || '',
    quantity,
    selected: true,
  }

  if (payload.qty === undefined || payload.qty === null || payload.qty === '') {
    payload.qty = Math.max(parseNumberField(food?.baseQty || quantity || 1), 0)
  }

  ;[
    ['calories', food?.baseCalories],
    ['protein', food?.baseProtein],
    ['carbs', food?.baseCarbs],
    ['fat', food?.baseFat],
  ].forEach(([key, fallback]) => {
    if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
      payload[key] = parseNumberField(fallback)
    }
  })

  return payload
}

const buildRecipeCreatePayload = (form) => ({
  name: form.name.trim(),
  video_url: form.video_url.trim(),
  unit: form.unit.trim(),
  qty: form.qty === '' ? '' : `${form.qty}`.trim(),
  note: form.note.trim(),
  description: form.description.trim(),
  recipecategory: form.recipetemplatecategory_id
    ? Number(form.recipetemplatecategory_id)
    : form.newCategoryName.trim(),
  subcat: form.newCategoryParentId ? Number(form.newCategoryParentId) : -1,
  foods: (form.recipeFoods || []).map(buildRecipeFoodCreatePayload),
})

const buildRecipeUpdatePayload = (form) => ({
  name: form.name.trim(),
  video_url: form.video_url.trim(),
  unit: form.unit.trim(),
  qty: form.qty === '' ? '' : `${form.qty}`.trim(),
  note: form.note.trim(),
  description: form.description.trim(),
  instructions: form.instructions.trim(),
  recipecategory: form.recipetemplatecategory_id ? Number(form.recipetemplatecategory_id) : '',
})

const buildFoodPayload = (form, categoryId) => {
  const payload = {
    name: form.name.trim(),
    description: form.description.trim(),
    note: form.note.trim(),
    video_url: form.video_url.trim(),
    qty: parseNumberField(form.qty),
    unit: form.unit.trim(),
    foodcategory: categoryId,
  }

  if (form.foodtemplatecategory_id) {
    payload.foodtemplatecategory_id = Number(form.foodtemplatecategory_id)
  }

  FOOD_MACRO_FIELDS.concat(FOOD_MICRO_FIELDS).forEach((field) => {
    payload[field.key] = parseNumberField(form[field.key])
  })

  return payload
}

const buildExerciseCreatePayload = (form) => ({
  name: form.name.trim(),
  exercisename: form.name.trim(),
  subcat: form.newCategoryParentId ? Number(form.newCategoryParentId) : -1,
  description: form.description.trim(),
  video_url: form.video_url.trim(),
  note: form.note.trim(),
  exercisecategory: form.exercisetemplatecategory_id
    ? Number(form.exercisetemplatecategory_id)
    : form.newCategoryName.trim(),
})

const buildExerciseUpdatePayload = (form) => {
  const payload = {
    name: form.name.trim(),
    description: form.description.trim(),
    video_url: form.video_url.trim(),
    note: form.note.trim(),
  }

  if (form.exercisetemplatecategory_id) {
    payload.exercisetemplatecategory_id = Number(form.exercisetemplatecategory_id)
  }

  return payload
}

const flattenExerciseCategories = (tree) => {
  const rows = []

  const visit = (node, depth = 0) => {
    rows.push({
      ...node,
      depth,
      parentcategory: node.parentcategory || null,
    })

    if (Array.isArray(node.children)) {
      node.children.forEach((child) => visit(child, depth + 1))
    }
  }

  ;(tree || []).forEach((node) => visit(node))
  return rows
}

const getTopLevelExerciseCategories = (tree) =>
  (tree || []).map((node) => ({
    ...node,
    depth: 0,
    parentcategory: node.parentcategory || null,
  }))

const EmptyState = ({ message }) => (
  <div className="uploader-empty">
    {message}
  </div>
)

const PageHero = ({ title, description, breadcrumb, onBack }) => (
  <div>
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="mb-2 inline-flex items-center gap-1.5 text-[13px] text-slate-500 transition-colors hover:text-slate-900"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
      ) : null}
      {breadcrumb ? <p className="mb-1 text-xs text-slate-500">{breadcrumb}</p> : null}
    <h1 className="text-[1.625rem] font-semibold leading-tight tracking-[-0.025em] text-slate-900">{title}</h1>
    <p className="mt-1 max-w-3xl text-sm text-slate-500">{description}</p>
  </div>
)

const WorkspaceTabs = ({ tabs, activeTab, onChange }) => (
  <div className="uploader-tabs">
    {tabs.map((tab) => {
      const Icon = tab.icon
      const isActive = activeTab === tab.id

      return (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={cn(
            'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition',
            isActive
              ? 'bg-fitness-primary text-primary-foreground shadow-sm'
              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          )}
        >
          <Icon className="h-4 w-4" />
          {tab.label}
        </button>
      )
    })}
  </div>
)

const NumericFieldGrid = ({ fields, form, onChange }) => (
  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
    {fields.map((field) => (
      <div key={field.key} className="space-y-2">
        <label className="text-sm font-medium text-slate-700">{field.label}</label>
        <Input
          type="number"
          step="any"
          min="0"
          value={form[field.key]}
          onChange={(event) => onChange(field.key, event.target.value)}
          placeholder={field.label}
        />
      </div>
    ))}
  </div>
)

const WorkspacePicker = ({ onSelect }) => (
  <div className="uploader-page">
    <PageHero
      title="Uploader Workspace"
      description="Choose the catalog you want to manage. Each workspace follows the same create, edit, and organization flow used across the rest of the admin app."
    />

    <div className="uploader-surface">
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {WORKSPACES.map((workspace) => {
          const Icon = workspace.icon

          return (
            <Card
              key={workspace.id}
              className="uploader-workspace-card hover:border-fitness-primary/30"
              onClick={() => onSelect(workspace.id)}
            >
              <CardContent className="flex items-start gap-4 p-6">
                <div className="rounded-2xl bg-fitness-primary/10 p-3 text-fitness-primary">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-slate-900">{workspace.title}</h3>
                  <p className="text-sm leading-6 text-slate-600">{workspace.description}</p>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  </div>
)

const FoodTemplateForm = ({
  form,
  categories,
  searchTerm,
  onSearchTermChange,
  searchResults,
  expandedResultId,
  onFetchDetails,
  detailedOptionsMap,
  onPrefill,
  onFieldChange,
  onImageFilesChange,
  onRecipeFileChange,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting,
  mode,
}) => {
  const mediaCount = form.imageFiles.length + (form.recipeFile ? 1 : 0)
  const [formSection, setFormSection] = useState('details')

  const showDetails = mode !== 'edit' || formSection === 'details'
  const showNutrition = mode !== 'edit' || formSection === 'nutrition'
  const showMedia = mode !== 'edit' || formSection === 'media'

  return (
    <div className="space-y-6">
      {mode === 'create' ? (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Search Existing Foods</CardTitle>
            <CardDescription>
              Search the current food template catalog and use a row as a starting point.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(event) => onSearchTermChange(event.target.value)}
                placeholder="Food search"
                className="pl-9"
              />
            </div>

            {searchTerm.trim() ? (
              searchResults.length ? (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Unit</th>
                        <th className="px-4 py-3">Serving Qty</th>
                        <th className="px-4 py-3">Cals</th>
                        <th className="px-4 py-3">Photo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {searchResults.map((row) => {
                        const isExpanded = expandedResultId === row.id
                        const options = detailedOptionsMap[row.id] || []

                        return (
                          <React.Fragment key={row.id}>
                            <tr
                              className="cursor-pointer transition hover:bg-slate-50"
                              onClick={() => onFetchDetails(row)}
                            >
                              <td className="px-4 py-3 font-medium text-slate-900">{row.name}</td>
                              <td className="px-4 py-3 text-slate-600">{row.unit || 'N/A'}</td>
                              <td className="px-4 py-3 text-slate-600">{formatNumberSmart(row.servingQty || row.qty)}</td>
                              <td className="px-4 py-3 text-slate-600">{formatNumberSmart(row.calories)}</td>
                              <td className="px-4 py-3">
                                {row.thumbnail ? (
                                  <img
                                    src={row.thumbnail}
                                    alt={row.name}
                                    className="h-10 w-10 rounded-lg border border-slate-200 object-cover shadow-sm"
                                  />
                                ) : (
                                  <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-slate-400">
                                    <ImageOff className="h-5 w-5" />
                                  </div>
                                )}
                              </td>
                            </tr>
                            {isExpanded ? (
                              <tr className="bg-slate-50/50">
                                <td colSpan={5} className="px-8 py-4">
                                  {options.length ? (
                                    <div className="space-y-4">
                                      {options.map((option, idx) => (
                                        <div key={idx} className="flex items-start gap-4">
                                          <button
                                            type="button"
                                            onClick={() => onPrefill(option)}
                                            className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-fitness-primary text-primary-foreground hover:bg-fitness-primary/90"
                                          >
                                            <Plus className="h-4 w-4" />
                                          </button>
                                          <div className="space-y-1">
                                            <p className="text-sm font-medium text-slate-900">
                                              {option.food_name || option.name} - {option.serving_qty || option.qty} {option.serving_unit || option.unit || option.measure}
                                            </p>
                                            <p className="text-xs text-slate-500 leading-relaxed">
                                              Calories: {formatNumberSmart(option.nf_calories || option.calories)} Protein: {formatNumberSmart(option.nf_protein || option.protein)} Carbs: {formatNumberSmart(option.nf_total_carbohydrate || option.carbs)} Fat: {formatNumberSmart(option.nf_total_fat || option.fat)} Sugars: {formatNumberSmart(option.nf_sugars || option.sugars)} Fiber: {formatNumberSmart(option.nf_dietary_fiber || option.fiber)} Calcium: {formatNumberSmart(option.nf_calcium || option.calcium)} Iron: {formatNumberSmart(option.nf_iron || option.iron)} Magnesium: {formatNumberSmart(option.nf_magnesium || option.magnesium)} Phosphorus: {formatNumberSmart(option.nf_p || option.phosphorus)} Potassium: {formatNumberSmart(option.nf_potassium || option.potassium)} Sodium: {formatNumberSmart(option.nf_sodium || option.sodium)} Zinc: {formatNumberSmart(option.nf_zinc || option.zinc)} Vitamin A: {formatNumberSmart(option.nf_vitamin_a_dv || option.vitamin_a)} Vitamin E: {formatNumberSmart(option.nf_vitamin_e || option.vitamin_e)} Vitamin D: {formatNumberSmart(option.nf_vitamin_d || option.vitamin_d)} Vitamin D3: {formatNumberSmart(option.nf_vitamin_d3 || option.vitamin_d3)} Vitamin C: {formatNumberSmart(option.nf_vitamin_c || option.vitamin_c)} Thiamin: {formatNumberSmart(option.nf_thiamin || option.thiamin)} Riboflavin: {formatNumberSmart(option.nf_riboflavin || option.riboflavin)} Niacin: {formatNumberSmart(option.nf_niacin || option.niacin)} Pantothenic Acid: {formatNumberSmart(option.nf_pantothenic_acid || option.pantothenic_acid)} Vitamin B6: {formatNumberSmart(option.nf_vitamin_b6 || option.vitamin_b6)} Folate: {formatNumberSmart(option.nf_folate || option.folate)} Vitamin B12: {formatNumberSmart(option.nf_vitamin_b12 || option.vitamin_b12)} Leucine: {formatNumberSmart(option.nf_leucine || option.leucine)} Cholesterol: {formatNumberSmart(option.nf_cholesterol || option.cholesterol)} Fatty Acids: {formatNumberSmart(option.nf_fatty_acids || option.fatty_acids || option.nf_saturated_fat)} DHA: {formatNumberSmart(option.nf_dha || option.dha)} EPA: {formatNumberSmart(option.nf_epa || option.epa)} ALA: {formatNumberSmart(option.nf_ala || option.ala)}
                                            </p>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-center py-4">
                                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState message="No food templates matched the current search." />
              )
            ) : (
              <p className="text-sm text-slate-500">Start typing to search the food template library.</p>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <CardTitle className="text-base font-semibold">{mode === 'create' ? 'Template Details' : 'Edit Food Template'}</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                {mode === 'create' ? 'Complete core details, nutrition values, and optional media.' : 'Update details, nutrition values, and media for this template.'}
              </CardDescription>
            </div>
            {mode === 'edit' ? (
              <div className="flex shrink-0 rounded-lg bg-slate-100 p-0.5">
                {[
                  { id: 'details', label: 'Details' },
                  { id: 'nutrition', label: 'Nutrition' },
                  { id: 'media', label: 'Media' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setFormSection(tab.id)}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-xs font-semibold transition-all',
                      formSection === tab.id
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">

          {showDetails ? (
            <>
              {/* Identity row: Name + Category */}
              <div className="grid gap-4 md:grid-cols-[minmax(0,1.6fr),minmax(0,1fr)]">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600">Name</label>
                  <Input value={form.name} onChange={(event) => onFieldChange('name', event.target.value)} placeholder="Name" className="h-9" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600">Food Category</label>
                  <select
                    className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                    value={form.foodtemplatecategory_id}
                    onChange={(event) => onFieldChange('foodtemplatecategory_id', event.target.value)}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Serving + Video row */}
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600">Default Qty</label>
                  <Input type="number" step="any" min="0" value={form.qty} onChange={(event) => onFieldChange('qty', event.target.value)} placeholder="0" className="h-9" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600">Unit</label>
                  <Input value={form.unit} onChange={(event) => onFieldChange('unit', event.target.value)} placeholder="g / oz / cup" className="h-9" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600">Video URL</label>
                  <Input value={form.video_url} onChange={(event) => onFieldChange('video_url', event.target.value)} placeholder="https://…" className="h-9" />
                </div>
              </div>

              {/* Create-only: new category fields */}
              {mode === 'create' ? (
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-600">Make Subcategory Of</label>
                    <select
                      className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                      value={form.newCategoryParentId}
                      onChange={(event) => onFieldChange('newCategoryParentId', event.target.value)}
                    >
                      <option value="">No parent category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-600">New Food Category</label>
                    <Input value={form.newCategoryName} onChange={(event) => onFieldChange('newCategoryName', event.target.value)} placeholder="Create a new category if needed" className="h-9" />
                  </div>
                </div>
              ) : null}

              {/* Description + Note */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600">Description</label>
                  <Textarea value={form.description} onChange={(event) => onFieldChange('description', event.target.value)} placeholder="Description" className="min-h-[80px] text-sm" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600">Note</label>
                  <Textarea value={form.note} onChange={(event) => onFieldChange('note', event.target.value)} placeholder="Note" className="min-h-[80px] text-sm" />
                </div>
              </div>
            </>
          ) : null}

          {showNutrition ? (
            <>
              {/* Macronutrients — highlighted strip */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Macronutrients</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {FOOD_MACRO_FIELDS.map((field) => (
                    <div key={field.key} className="space-y-1.5">
                      <label className="text-xs font-medium text-slate-600">{field.label}</label>
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={form[field.key]}
                        onChange={(event) => onFieldChange(field.key, event.target.value)}
                        placeholder="0"
                        className="h-9 text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Micronutrients — grouped */}
              <div className="space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Micronutrients</p>
                {FOOD_MICRO_GROUPS.map((group) => {
                  const groupFields = FOOD_MICRO_FIELDS.filter((f) => group.keys.includes(f.key))
                  return (
                    <div key={group.label} className="rounded-xl border border-slate-100 bg-white p-3 space-y-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{group.label}</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                        {groupFields.map((field) => (
                          <div key={field.key} className="space-y-1">
                            <label className="text-xs font-medium text-slate-600">{field.label}</label>
                            <Input
                              type="number"
                              step="any"
                              min="0"
                              value={form[field.key]}
                              onChange={(event) => onFieldChange(field.key, event.target.value)}
                              placeholder="0"
                              className="h-8 text-xs"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          ) : null}

          {showMedia ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Media Attachments</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600">Image Sequence</label>
                  <Input type="file" multiple accept="image/*" onChange={onImageFilesChange} className="h-9 bg-white text-xs" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600">Recipe Document</label>
                  <Input type="file" onChange={onRecipeFileChange} className="h-9 bg-white text-xs" />
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-4">
            {onCancel ? (
              <Button type="button" variant="outline" size="sm" onClick={onCancel}>
                Cancel
              </Button>
            ) : null}
            <Button type="button" size="sm" onClick={onSubmit} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {submitLabel}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

const ExerciseTemplateForm = ({
  form,
  categories,
  parentCategories = categories,
  onFieldChange,
  onImageFilesChange,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting,
  mode,
}) => (
  <Card className="border-slate-200 shadow-sm">
    <CardHeader>
      <CardTitle className="text-lg">
        {mode === 'create' ? 'Create Exercise Template' : 'Edit Exercise Template'}
      </CardTitle>
      <CardDescription>
        {mode === 'create'
          ? 'Add a template, assign it to an exercise category, and attach image sequence media if needed.'
          : 'Update the template details, category assignments, and media attachments.'}
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Name</label>
        <Input
          value={form.name}
          onChange={(event) => onFieldChange('name', event.target.value)}
          placeholder="Name"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Exercise Category</label>
        <select
          className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
          value={form.exercisetemplatecategory_id}
          onChange={(event) => onFieldChange('exercisetemplatecategory_id', event.target.value)}
        >
          <option value="">Select category</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {`${'  '.repeat(category.depth || 0)}${category.name}`}
            </option>
          ))}
        </select>
      </div>

      {mode === 'create' ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Make Subcategory Of</label>
            <select
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
              value={form.newCategoryParentId}
              onChange={(event) => onFieldChange('newCategoryParentId', event.target.value)}
            >
              <option value="">No parent category</option>
              {parentCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">New Exercise Category</label>
            <Input
              value={form.newCategoryName}
              onChange={(event) => onFieldChange('newCategoryName', event.target.value)}
              placeholder="Create a new category if needed"
            />
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Description</label>
        <Textarea
          value={form.description}
          onChange={(event) => onFieldChange('description', event.target.value)}
          placeholder="Description"
          className="min-h-[100px]"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Note</label>
        <Textarea
          value={form.note}
          onChange={(event) => onFieldChange('note', event.target.value)}
          placeholder="Note"
          className="min-h-[100px]"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Video URL</label>
        <Input
          value={form.video_url}
          onChange={(event) => onFieldChange('video_url', event.target.value)}
          placeholder="Video URL"
        />
      </div>

      <div className="space-y-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-slate-900">Upload Image Sequence</p>
          {mode === 'edit' && (
            <Badge variant="outline" className="border-slate-300 text-slate-600">
              Optional
            </Badge>
          )}
        </div>
        <Input type="file" multiple accept="image/*" onChange={onImageFilesChange} />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {onCancel ? (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button type="button" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          {submitLabel}
        </Button>
      </div>
    </CardContent>
  </Card>
)

const MealTemplateDetailForm = ({
  form,
  categories,
  foods = [],
  onFieldChange,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting,
}) => {
  const summary = calculateMealMacroTotals(foods)

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg">Edit Meal Template</CardTitle>
        <CardDescription>Update the template details and meal category assignment.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Name</label>
          <Input
            value={form.name}
            onChange={(event) => onFieldChange('name', event.target.value)}
            placeholder="Name"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Meal Category</label>
          <select
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
            value={form.mealtemplatecategory_id}
            onChange={(event) => onFieldChange('mealtemplatecategory_id', event.target.value)}
          >
            <option value="">Select category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Description</label>
          <Textarea
            value={form.description}
            onChange={(event) => onFieldChange('description', event.target.value)}
            placeholder="Description"
            className="min-h-[100px]"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Note</label>
          <Textarea
            value={form.note}
            onChange={(event) => onFieldChange('note', event.target.value)}
            placeholder="Note"
            className="min-h-[100px]"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Video URL</label>
          <Input
            value={form.video_url}
            onChange={(event) => onFieldChange('video_url', event.target.value)}
            placeholder="Video URL"
          />
        </div>

        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
          <p className="text-sm font-medium text-slate-900">Meal Summary</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              ['Foods', foods.length],
              ['Calories', formatNumberSmart(summary.calories)],
              ['Protein', formatNumberSmart(summary.protein)],
              ['Carbs', formatNumberSmart(summary.carbs)],
              ['Fat', formatNumberSmart(summary.fat)],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
                <p className="mt-1 text-base font-semibold text-slate-900">{value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {submitLabel}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

const RecipeTemplateEditor = ({
  form,
  categories,
  foods,
  onFieldChange,
  onImageFilesChange,
  onReset,
  onSubmit,
  submitLabel,
  isSubmitting,
  activeFoodId = null,
  onActivateFood = null,
  onFoodQtyChange,
  onDeleteFood,
  nutritionSearchTerm,
  onNutritionSearchTermChange,
  nutritionLoading,
  nutritionTotalCount,
  nutritionRows,
  nutritionPageIndex,
  nutritionPageSize,
  onNutritionPageChange,
  expandedNutritionResultId,
  onToggleNutritionResult,
  nutritionDetailMap,
  nutritionDetailLoadingId,
  onAddNutritionFood,
  foodTemplateRows,
  foodTemplateLoading,
  foodTemplatePageIndex,
  foodTemplatePageSize,
  foodTemplateTotalCount,
  onFoodTemplatePageChange,
  onAddFoodTemplate,
  foodTemplateSearchTerm,
  onFoodTemplateSearchTermChange,
  onCopyExistingRecipe,
}) => {
  const summary = calculateMealMacroTotals(foods)

  return (
    <div className="space-y-6">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-lg">Create Recipe Template</CardTitle>
            <CardDescription>Define the recipe metadata, build its ingredient list, and review macro totals.</CardDescription>
          </div>
          <Button type="button" variant="outline" onClick={onCopyExistingRecipe}>
            Copy Existing Recipe
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr),minmax(320px,0.8fr)]">
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Name</label>
                <Input value={form.name} onChange={(event) => onFieldChange('name', event.target.value)} placeholder="Name" />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Recipe Category</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                    value={form.recipetemplatecategory_id}
                    onChange={(event) => onFieldChange('recipetemplatecategory_id', event.target.value)}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Make Subcategory Of</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                    value={form.newCategoryParentId}
                    onChange={(event) => onFieldChange('newCategoryParentId', event.target.value)}
                  >
                    <option value="">No parent category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">New Recipe Category</label>
                <Input
                  value={form.newCategoryName}
                  onChange={(event) => onFieldChange('newCategoryName', event.target.value)}
                  placeholder="New recipe category..."
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Description</label>
                <Textarea
                  value={form.description}
                  onChange={(event) => onFieldChange('description', event.target.value)}
                  placeholder="Description"
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Instructions</label>
                <Textarea
                  value={form.instructions}
                  onChange={(event) => onFieldChange('instructions', event.target.value)}
                  placeholder="Instructions"
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Note</label>
                <Textarea
                  value={form.note}
                  onChange={(event) => onFieldChange('note', event.target.value)}
                  placeholder="Note"
                  className="min-h-[100px]"
                />
              </div>
            </div>

            <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Video URL</label>
                <Input
                  value={form.video_url}
                  onChange={(event) => onFieldChange('video_url', event.target.value)}
                  placeholder="Video URL"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Recipe Unit Name</label>
                  <Input
                    value={form.unit}
                    onChange={(event) => onFieldChange('unit', event.target.value)}
                    placeholder="Servings"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Default Qty</label>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    value={form.qty}
                    onChange={(event) => onFieldChange('qty', event.target.value)}
                    placeholder="Total servings in recipe"
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Upload Image Sequence</p>
                    <p className="text-xs text-slate-500">Recipe uploads are UI-only until write APIs are mapped.</p>
                  </div>
                  <Badge variant="outline" className="border-slate-300 text-slate-600">
                    Draft Only
                  </Badge>
                </div>

                <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(form.useCopiedRecipeImages)}
                    onChange={(event) => onFieldChange('useCopiedRecipeImages', event.target.checked)}
                  />
                  Use images from copied recipe
                </label>

                <Input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={onImageFilesChange}
                  disabled={Boolean(form.useCopiedRecipeImages)}
                  className="mt-4 bg-white"
                />
              </div>

              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4">
                <p className="text-sm font-medium text-slate-900">Recipe Summary</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {[
                    ['Ingredients', foods.length],
                    ['Calories', formatNumberSmart(summary.calories)],
                    ['Protein', formatNumberSmart(summary.protein)],
                    ['Carbs', formatNumberSmart(summary.carbs)],
                    ['Fat', formatNumberSmart(summary.fat)],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-slate-200 px-4 py-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
                      <p className="mt-1 text-base font-semibold text-slate-900">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-slate-900">Recipe Ingredients</h3>
            <MealFoodsTable
              foods={foods}
              activeFoodId={activeFoodId}
              onActivateFood={onActivateFood}
              onQtyChange={onFoodQtyChange}
              onDeleteFood={onDeleteFood}
            />
          </div>

          <NutritionSearchTable
            searchTerm={nutritionSearchTerm}
            onSearchTermChange={onNutritionSearchTermChange}
            loading={nutritionLoading}
            totalCount={nutritionTotalCount}
            rows={nutritionRows}
            pageIndex={nutritionPageIndex}
            pageSize={nutritionPageSize}
            onPageChange={onNutritionPageChange}
            expandedRowId={expandedNutritionResultId}
            onToggleRow={onToggleNutritionResult}
            detailMap={nutritionDetailMap}
            detailLoadingId={nutritionDetailLoadingId}
            onAddNutritionFood={onAddNutritionFood}
          />

          <FoodTemplateLibraryTable
            rows={foodTemplateRows}
            totalCount={foodTemplateTotalCount}
            loading={foodTemplateLoading}
            pageIndex={foodTemplatePageIndex}
            pageSize={foodTemplatePageSize}
            onPageChange={onFoodTemplatePageChange}
            onAddFoodTemplate={onAddFoodTemplate}
            title="Add Food Template"
            description="Search saved food templates and add them into the recipe ingredient list."
            searchTerm={foodTemplateSearchTerm}
            onSearchTermChange={onFoodTemplateSearchTermChange}
          />

          <div className="flex flex-wrap items-center justify-end gap-3">
            <Button type="button" variant="outline" onClick={onReset}>
              Reset Changes
            </Button>
            <Button type="button" onClick={onSubmit} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {submitLabel}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

const RecipeTemplateDetailForm = ({
  form,
  categories,
  onFieldChange,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting,
}) => (
  <Card className="border-slate-200 shadow-sm">
    <CardHeader>
      <CardTitle className="text-lg">Edit Recipe Template</CardTitle>
      <CardDescription>Review the selected recipe template and adjust the draft metadata locally.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Name</label>
        <Input value={form.name} onChange={(event) => onFieldChange('name', event.target.value)} placeholder="Name" />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Recipe Category</label>
        <select
          className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
          value={form.recipetemplatecategory_id}
          onChange={(event) => onFieldChange('recipetemplatecategory_id', event.target.value)}
        >
          <option value="">Select category</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Description</label>
        <Textarea
          value={form.description}
          onChange={(event) => onFieldChange('description', event.target.value)}
          placeholder="Description"
          className="min-h-[100px]"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Instructions</label>
        <Textarea
          value={form.instructions}
          onChange={(event) => onFieldChange('instructions', event.target.value)}
          placeholder="Instructions"
          className="min-h-[140px]"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Note</label>
        <Textarea
          value={form.note}
          onChange={(event) => onFieldChange('note', event.target.value)}
          placeholder="Note"
          className="min-h-[100px]"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Video URL</label>
          <Input
            value={form.video_url}
            onChange={(event) => onFieldChange('video_url', event.target.value)}
            placeholder="Video URL"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Recipe Unit</label>
          <Input value={form.unit} onChange={(event) => onFieldChange('unit', event.target.value)} placeholder="Servings" />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Default Qty</label>
        <Input
          type="number"
          step="any"
          min="0"
          value={form.qty}
          onChange={(event) => onFieldChange('qty', event.target.value)}
          placeholder="Total servings in recipe"
        />
      </div>

      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
        Ingredient-level recipe detail endpoints were not provided, so this editor currently uses the recipe catalog metadata only.
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          {submitLabel}
        </Button>
      </div>
    </CardContent>
  </Card>
)

const PhotoCell = ({ src, alt }) => (
  <div className="flex justify-center">
    {src ? (
      <img src={src} alt={alt} className="h-12 w-12 rounded-xl border border-slate-200 object-cover" />
    ) : (
      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-slate-400">
        <ImageOff className="h-4 w-4" />
      </div>
    )}
  </div>
)

const PaginationFooter = ({
  pageIndex,
  pageSize,
  totalCount,
  onPageChange,
  pageSizeLabel = null,
  compact = false,
}) => {
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))
  const from = totalCount ? pageIndex * pageSize + 1 : 0
  const to = Math.min(totalCount, pageIndex * pageSize + pageSize)

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between',
        compact && 'rounded-xl'
      )}
    >
      <p>
        Showing <span className="font-medium text-slate-900">{from}-{to}</span> of{' '}
        <span className="font-medium text-slate-900">{totalCount}</span>
        {pageSizeLabel ? <span className="ml-2 text-slate-500">{pageSizeLabel}</span> : null}
      </p>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onPageChange(Math.max(0, pageIndex - 1))}
          disabled={pageIndex === 0}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-[88px] text-center">
          Page {Math.min(pageIndex + 1, pageCount)} of {pageCount}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onPageChange(Math.min(pageCount - 1, pageIndex + 1))}
          disabled={pageIndex >= pageCount - 1}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

const CollapsedCatalogCard = ({ label, onExpand, title = 'Editing' }) => (
  <Card className="border-slate-200 shadow-sm xl:sticky xl:top-6">
    <CardContent className="flex flex-col items-center gap-4 p-3">
      <button
        type="button"
        onClick={onExpand}
        className="flex h-11 w-11 items-center justify-center rounded-2xl border border-fitness-primary/20 bg-fitness-primary/10 text-fitness-primary transition hover:bg-fitness-primary/15"
        title="Browse catalog"
      >
        <LayoutGrid className="h-5 w-5" />
      </button>
      <div className="w-full rounded-2xl bg-slate-50 px-2 py-3 text-center">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{'Editing'}</p>
        <p className="mt-2 break-words text-xs font-semibold text-slate-900">{label || 'Template'}</p>
      </div>
    </CardContent>
  </Card>
)

const MealFoodsTable = ({
  foods,
  selectedFoodIds = [],
  onSelectFood = null,
  onSelectAllFoods = null,
  activeFoodId = null,
  onActivateFood = null,
  onQtyChange,
  onDeleteFood,
  allowQtyEdit = true,
  showSelection = false,
  expandedFoodId = null,
  onToggleFoodDetail = null,
  foodDetailRows = {},
  foodDetailLoadingId = null,
}) => {
  const totals = calculateMealMacroTotals(foods)
  const columnCount = 6 + (showSelection ? 1 : 0) + (onToggleFoodDetail ? 1 : 0)

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200">
      <table className="w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            {showSelection ? (
              <th className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={foods.length > 0 && foods.every((food) => selectedFoodIds.includes(food.id))}
                  onChange={(event) => onSelectAllFoods?.(event.target.checked)}
                />
              </th>
            ) : null}
            {onToggleFoodDetail ? <th className="px-4 py-3" /> : null}
            <th className="px-4 py-3">Food</th>
            <th className="px-4 py-3">Calories</th>
            <th className="px-4 py-3">Protein</th>
            <th className="px-4 py-3">Carbs</th>
            <th className="px-4 py-3">Fat</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {foods.length ? (
            foods.map((food) => {
              const macros = getScaledMealFoodMacros(food)
              const detailRows = foodDetailRows[food.id] || []
              const isExpanded = expandedFoodId === food.id

              return (
                <React.Fragment key={food.id}>
                  <tr
                    className={cn('cursor-pointer hover:bg-slate-50', activeFoodId === food.id && 'bg-fitness-primary/5')}
                    onClick={() => onActivateFood?.(food.id)}
                  >
                    {showSelection ? (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedFoodIds.includes(food.id)}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => onSelectFood?.(food.id)}
                        />
                      </td>
                    ) : null}
                    {onToggleFoodDetail ? (
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            onToggleFoodDetail(food)
                          }}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
                        >
                          {foodDetailLoadingId === food.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                    ) : null}
                    <td className="px-4 py-3 align-top">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900">{food.name}</p>
                        <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">
                          {food.category || 'Common food'}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span>{food.unit || 'N/A'}</span>
                          {allowQtyEdit ? (
                            <Input
                              type="number"
                              step="any"
                              min="0"
                              value={food.qty}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event) => onQtyChange(food.id, event.target.value)}
                              className="h-8 w-24"
                            />
                          ) : (
                            <span>Qty {formatNumberSmart(food.qty)}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatNumberSmart(macros.calories)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatNumberSmart(macros.protein)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatNumberSmart(macros.carbs)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatNumberSmart(macros.fat)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={(event) => {
                          event.stopPropagation()
                          onDeleteFood(food.id)
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-slate-500" />
                      </Button>
                    </td>
                  </tr>

                  {isExpanded ? (
                    <tr className="bg-slate-50/80">
                      <td
                        className="px-4 py-4"
                        colSpan={columnCount}
                      >
                        {detailRows.length ? (
                          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                            {detailRows.map((row) => (
                              <div key={`${food.id}-${row.key}`} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                                <p className="text-xs uppercase tracking-wide text-slate-500">{row.label}</p>
                                <p className="mt-1 text-sm font-medium text-slate-900">{row.value}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">No detail fields returned for this food.</p>
                        )}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              )
            })
          ) : (
            <tr>
              <td
                className="px-4 py-12 text-center text-sm text-slate-500"
                colSpan={columnCount}
              >
                No foods have been added to this meal yet.
              </td>
            </tr>
          )}

          <tr className="bg-slate-50 font-semibold text-slate-900">
            <td className="px-4 py-4" colSpan={1 + (showSelection ? 1 : 0) + (onToggleFoodDetail ? 1 : 0)}>
              Macro totals
            </td>
            <td className="px-4 py-4">{formatNumberSmart(totals.calories)}</td>
            <td className="px-4 py-4">{formatNumberSmart(totals.protein)}</td>
            <td className="px-4 py-4">{formatNumberSmart(totals.carbs)}</td>
            <td className="px-4 py-4">{formatNumberSmart(totals.fat)}</td>
            <td className="px-4 py-4">
              &nbsp;
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const NutritionSearchTable = ({
  searchTerm,
  onSearchTermChange,
  loading,
  totalCount,
  rows,
  pageIndex,
  pageSize,
  onPageChange,
  expandedRowId,
  onToggleRow,
  detailMap,
  detailLoadingId,
  onAddNutritionFood,
}) => (
  <Card className="border-slate-200 shadow-sm">
    <CardHeader>
      <CardTitle className="text-lg">Add Nutritionix Food</CardTitle>
      <CardDescription>Search external foods, expand a row to load nutrient detail, then add it into the meal.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={searchTerm}
          onChange={(event) => onSearchTermChange(event.target.value)}
          placeholder="Food Search"
          className="pl-9"
        />
      </div>

      {loading ? (
        <EmptyState message="Searching foods..." />
      ) : searchTerm.trim() ? (
        rows.length ? (
          <>
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3" />
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Unit</th>
                    <th className="px-4 py-3">Serving Qty</th>
                    <th className="px-4 py-3">Cals</th>
                    <th className="px-4 py-3">Photo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {rows.map((row) => {
                    const detail = detailMap[row.id]
                    const isExpanded = expandedRowId === row.id

                    return (
                      <React.Fragment key={row.id}>
                        <tr className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => onToggleRow(row)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
                            >
                              {detailLoadingId === row.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-900">{row.name}</td>
                          <td className="px-4 py-3 text-slate-600">{row.unit || 'N/A'}</td>
                          <td className="px-4 py-3 text-slate-600">{formatNumberSmart(row.servingQty)}</td>
                          <td className="px-4 py-3 text-slate-600">{formatNumberSmart(row.calories)}</td>
                          <td className="px-4 py-3">
                            <PhotoCell src={row.thumbnail} alt={row.name} />
                          </td>
                        </tr>

                        {isExpanded ? (
                          <tr className="bg-slate-50/80">
                            <td className="px-4 py-4" colSpan={6}>
                              {detail ? (
                                <div className="space-y-4">
                                  <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 lg:flex-row lg:items-start lg:justify-between">
                                    <div className="flex items-start gap-4">
                                      <PhotoCell src={detail.photo?.thumb || detail.thumbnail} alt={detail.food_name || row.name} />
                                      <div className="space-y-1">
                                        <h4 className="text-base font-semibold text-slate-900">
                                          {detail.food_name || detail.name || row.name}
                                        </h4>
                                        <p className="text-sm text-slate-500">
                                          {detail.brand_name || row.brandName || 'Common food'}
                                        </p>
                                        <p className="text-sm text-slate-500">
                                          {formatNumberSmart(detail.serving_qty || detail.qty || 1)} {detail.serving_unit || detail.unit || row.unit}
                                        </p>
                                      </div>
                                    </div>

                                    {/* <Button
                                      type="button"
                                      onClick={() =>
                                        onAddNutritionFood(detail, {
                                          qty: detail.serving_qty || detail.qty || 1,
                                          measure: detail.serving_unit || detail.unit || 'serving',
                                          serving_weight: detail.serving_weight_grams,
                                          calories: detail.calories ?? detail.nf_calories,
                                          protein: detail.protein ?? detail.nf_protein,
                                          carbs: detail.carbs ?? detail.nf_total_carbohydrate,
                                          fat: detail.fat ?? detail.nf_total_fat,
                                        })
                                      }
                                    >
                                      <Plus className="mr-2 h-4 w-4" />
                                      Add To Meal
                                    </Button> */}
                                  </div>

                                  <div className="space-y-3">
                                    <div>
                                      <p className="text-sm font-medium text-slate-900">Serving Options</p>
                                      <p className="text-xs text-slate-500">Choose the measure you want to add into the meal.</p>
                                    </div>
                                    <div className="space-y-3">
                                      {getNutritionMeasureOptions(detail).map((measure) => (
                                        <div
                                          key={`${row.id}-${measure.id}`}
                                          className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 lg:flex-row lg:items-center lg:justify-between"
                                        >
                                          <div className="space-y-2">
                                            <p className="text-base font-semibold text-slate-900">
                                              {detail.food_name || row.name} - {formatNumberSmart(measure.qty)} {measure.measure}
                                            </p>
                                            <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                                              <span>Calories: {formatNumberSmart(measure.calories)}</span>
                                              <span>Protein: {formatNumberSmart(measure.protein)}</span>
                                              <span>Carbs: {formatNumberSmart(measure.carbs)}</span>
                                              <span>Fat: {formatNumberSmart(measure.fat)}</span>
                                              {measure.serving_weight ? (
                                                <span>Weight: {formatNumberSmart(measure.serving_weight)} g</span>
                                              ) : null}
                                            </div>
                                          </div>

                                          <Button type="button" variant="outline" onClick={() => onAddNutritionFood(detail, measure)}>
                                            <Plus className="mr-2 h-4 w-4" />
                                            Add {measure.measure}
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                                    {buildNutritionDetailMap(detail).map((field) => (
                                      <div key={`${row.id}-${field.key}`} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                                        <p className="text-xs uppercase tracking-wide text-slate-500">{field.label}</p>
                                        <p className="mt-1 text-sm font-medium text-slate-900">{field.value}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm text-slate-500">No nutrient detail available.</p>
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <PaginationFooter
              pageIndex={pageIndex}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={onPageChange}
              pageSizeLabel={`${pageSize} / page`}
              compact
            />
          </>
        ) : (
          <EmptyState message="No foods matched the current search." />
        )
      ) : (
        <p className="text-sm text-slate-500">Start typing to search Nutritionix foods.</p>
      )}
    </CardContent>
  </Card>
)

const FoodTemplateLibraryTable = ({
  rows,
  totalCount,
  loading,
  pageIndex,
  pageSize,
  onPageChange,
  onAddFoodTemplate,
  title = 'Add Food Template',
  description = 'Use saved food templates as meal ingredients and scale their macros with the meal quantity input.',
  searchTerm = '',
  onSearchTermChange = null,
  searchPlaceholder = 'Search Food Template',
}) => (
  <Card className="border-slate-200 shadow-sm">
    <CardHeader>
      <CardTitle className="text-lg">{title}</CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      {onSearchTermChange ? (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
            placeholder={searchPlaceholder}
            className="pl-9"
          />
        </div>
      ) : null}

      {loading ? (
        <EmptyState message="Loading food templates..." />
      ) : rows.length ? (
        <>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Calories</th>
                  <th className="px-4 py-3">Protein</th>
                  <th className="px-4 py-3">Carbs</th>
                  <th className="px-4 py-3">Fat</th>
                  <th className="px-4 py-3">Unit</th>
                  <th className="px-4 py-3">Quantity</th>
                  <th className="px-4 py-3 text-right">Add</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{row.name}</td>
                    <td className="px-4 py-3 text-slate-600">{row.foodtemplatecategory?.name || 'Uncategorized'}</td>
                    <td className="px-4 py-3 text-slate-600">{formatNumberSmart(row.calories)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatNumberSmart(row.protein)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatNumberSmart(row.carbs)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatNumberSmart(row.fat)}</td>
                    <td className="px-4 py-3 text-slate-600">{row.unit || 'N/A'}</td>
                    <td className="px-4 py-3 text-slate-600">{formatNumberSmart(row.qty)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button type="button" size="icon" variant="outline" onClick={() => onAddFoodTemplate(row)}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PaginationFooter
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={onPageChange}
            pageSizeLabel={`${pageSize} / page`}
            compact
          />
        </>
      ) : (
        <EmptyState message="No food templates are available yet." />
      )}
    </CardContent>
  </Card>
)

const MealTemplateEditor = ({
  mode,
  form,
  categories,
  onFieldChange,
  onReset,
  onSubmit,
  submitLabel,
  isSubmitting,
  foods,
  selectedFoodIds = [],
  onSelectFood = null,
  onSelectAllFoods = null,
  onDeleteSelectedFoods = null,
  activeFoodId = null,
  activeFood = null,
  onActivateFood = null,
  onFoodFieldChange = null,
  onFoodQtyChange,
  onDeleteFood,
  onToggleMealFoodDetail = null,
  expandedMealFoodId = null,
  mealFoodDetailRows = {},
  mealFoodDetailLoadingId = null,
  nutritionSearchTerm,
  onNutritionSearchTermChange,
  nutritionLoading,
  nutritionTotalCount,
  nutritionRows,
  nutritionPageIndex,
  nutritionPageSize,
  onNutritionPageChange,
  expandedNutritionResultId,
  onToggleNutritionResult,
  nutritionDetailMap,
  nutritionDetailLoadingId,
  onAddNutritionFood,
  foodTemplateRows,
  foodTemplateLoading,
  foodTemplatePageIndex,
  foodTemplatePageSize,
  foodTemplateTotalCount,
  onFoodTemplatePageChange,
  onAddFoodTemplate,
}) => (
  <div className="space-y-6">
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg">
          {mode === 'create' ? 'Create Meal Template' : 'Edit Meal Template'}
        </CardTitle>
        <CardDescription>
          {mode === 'create'
            ? 'Set the meal metadata, add foods from templates or Nutritionix, then review the macro totals.'
            : 'Review the selected meal template, expand foods for detail, and adjust the draft meal composition.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 overflow-x-hidden">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.5fr),minmax(320px,0.9fr)]">
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Name</label>
              <Input value={form.name} onChange={(event) => onFieldChange('name', event.target.value)} placeholder="Name" />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Meal Category</label>
                <select
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                  value={form.mealtemplatecategory_id}
                  onChange={(event) => onFieldChange('mealtemplatecategory_id', event.target.value)}
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Make Subcategory Of</label>
                <select
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                  value={form.newCategoryParentId}
                  onChange={(event) => onFieldChange('newCategoryParentId', event.target.value)}
                  disabled={mode !== 'create'}
                >
                  <option value="">No parent category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {mode === 'create' ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">New Meal Category</label>
                <Input
                  value={form.newCategoryName}
                  onChange={(event) => onFieldChange('newCategoryName', event.target.value)}
                  placeholder="New meal category..."
                />
              </div>
            ) : null}

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea
                value={form.description}
                onChange={(event) => onFieldChange('description', event.target.value)}
                placeholder="Description"
                className="min-h-[120px]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Note</label>
              <Textarea
                value={form.note}
                onChange={(event) => onFieldChange('note', event.target.value)}
                placeholder="Note"
                className="min-h-[120px]"
              />
            </div>
          </div>

          <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Video URL</label>
              <Input
                value={form.video_url}
                onChange={(event) => onFieldChange('video_url', event.target.value)}
                placeholder="Video URL"
              />
            </div>

            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4">
              <p className="text-sm font-medium text-slate-900">Draft Summary</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {[
                  ['Foods', foods.length],
                  ['Calories', formatNumberSmart(calculateMealMacroTotals(foods).calories)],
                  ['Protein', formatNumberSmart(calculateMealMacroTotals(foods).protein)],
                  ['Carbs', formatNumberSmart(calculateMealMacroTotals(foods).carbs)],
                  ['Fat', formatNumberSmart(calculateMealMacroTotals(foods).fat)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border border-slate-200 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
                    <p className="mt-1 text-base font-semibold text-slate-900">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr),minmax(320px,0.8fr)]">
          <div className="min-w-0 space-y-6">
            <NutritionSearchTable
              searchTerm={nutritionSearchTerm}
              onSearchTermChange={onNutritionSearchTermChange}
              loading={nutritionLoading}
              totalCount={nutritionTotalCount}
              rows={nutritionRows}
              pageIndex={nutritionPageIndex}
              pageSize={nutritionPageSize}
              onPageChange={onNutritionPageChange}
              expandedRowId={expandedNutritionResultId}
              onToggleRow={onToggleNutritionResult}
              detailMap={nutritionDetailMap}
              detailLoadingId={nutritionDetailLoadingId}
              onAddNutritionFood={onAddNutritionFood}
            />

            {/* <FoodTemplateLibraryTable
              rows={foodTemplateRows}
              totalCount={foodTemplateTotalCount}
              loading={foodTemplateLoading}
              pageIndex={foodTemplatePageIndex}
              pageSize={foodTemplatePageSize}
              onPageChange={onFoodTemplatePageChange}
              onAddFoodTemplate={onAddFoodTemplate}
            /> */}
          </div>

          <div className="min-w-0 space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Food Detail</CardTitle>
                <CardDescription>
                  {activeFood ? 'Review the selected food row without leaving the editor.' : 'Select a food row to review its detail here.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {activeFood ? (
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-900">{activeFood.name || 'Food'}</p>
                      <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">{activeFood.category || activeFood.sourceType || 'Meal food'}</p>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Qty</label>
                        <Input
                          type="number"
                          step="any"
                          min="0"
                          value={activeFood.qty ?? ''}
                          onChange={(event) => onFoodQtyChange?.(activeFood.id, event.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Unit</label>
                        <Input
                          value={activeFood.unit || ''}
                          onChange={(event) => onFoodFieldChange?.('unit', event.target.value)}
                        />
                      </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Calories</label>
                        <Input value={activeFood.baseCalories ?? ''} onChange={(event) => onFoodFieldChange?.('baseCalories', event.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Protein</label>
                        <Input value={activeFood.baseProtein ?? ''} onChange={(event) => onFoodFieldChange?.('baseProtein', event.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Carbs</label>
                        <Input value={activeFood.baseCarbs ?? ''} onChange={(event) => onFoodFieldChange?.('baseCarbs', event.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fat</label>
                        <Input value={activeFood.baseFat ?? ''} onChange={(event) => onFoodFieldChange?.('baseFat', event.target.value)} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                    No food selected.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="min-w-0 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Meal Foods</h3>
              <p className="text-sm text-slate-500">Review the current foods in the meal and select one to edit on the right.</p>
            </div>
            {mode === 'edit' && onDeleteSelectedFoods ? (
              <Button
                type="button"
                variant="destructive"
                disabled={!selectedFoodIds.length}
                onClick={onDeleteSelectedFoods}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Selected Food(s)
              </Button>
            ) : null}
          </div>

          <MealFoodsTable
            foods={foods}
            selectedFoodIds={selectedFoodIds}
            onSelectFood={onSelectFood}
            onSelectAllFoods={onSelectAllFoods}
            activeFoodId={activeFoodId}
            onActivateFood={onActivateFood}
            onQtyChange={onFoodQtyChange}
            onDeleteFood={onDeleteFood}
            allowQtyEdit={mode === 'create'}
            showSelection={mode === 'edit'}
            expandedFoodId={expandedMealFoodId}
            onToggleFoodDetail={onToggleMealFoodDetail}
            foodDetailRows={mealFoodDetailRows}
            foodDetailLoadingId={mealFoodDetailLoadingId}
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={onReset}>
            Reset Changes
          </Button>
          <Button type="button" onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {submitLabel}
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
)

function FoodTemplatesWorkspace({ companyUrl, activeTab, onTabChange, onBack }) {
  const { toast } = useToast()
  const settingsTableRef = useRef(null)
  const [categories, setCategories] = useState([])
  const [settingsRows, setSettingsRows] = useState([])
  const [settingsFilter, setSettingsFilter] = useState('all')
  const [settingsNewCategoryName, setSettingsNewCategoryName] = useState('')
  const [createSearch, setCreateSearch] = useState('')
  const [createResults, setCreateResults] = useState([])
  const [expandedResultId, setExpandedResultId] = useState(null)
  const [detailedOptionsMap, setDetailedOptionsMap] = useState({})
  const [createForm, setCreateForm] = useState({ ...FOOD_DEFAULT_FORM })
  const [editRows, setEditRows] = useState([])
  const [editSearch, setEditSearch] = useState('')
  const [editPagination, setEditPagination] = useState({ ...EMPTY_EDIT_PAGINATION })
  const [editTotalCount, setEditTotalCount] = useState(0)
  const [editSelectedIds, setEditSelectedIds] = useState([])
  const [selectedTemplateId, setSelectedTemplateId] = useState(null)
  const [editForm, setEditForm] = useState({ ...FOOD_DEFAULT_FORM })
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingCreateSearch, setLoadingCreateSearch] = useState(false)
  const [savingCreate, setSavingCreate] = useState(false)
  const [loadingEditList, setLoadingEditList] = useState(false)
  const [loadingEditDetail, setLoadingEditDetail] = useState(false)
  const [savingEdit, setSavingEdit] = useState(false)
  const [deletingRows, setDeletingRows] = useState(false)
  const [loadingSettings, setLoadingSettings] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    type: null, // 'templates' | 'category'
    id: null, // for category delete
    title: '',
    description: '',
  })

  const editPageCount = Math.max(1, Math.ceil(editTotalCount / editPagination.pageSize))
  const visibleSettingsRows =
    settingsFilter === 'all'
      ? settingsRows
      : settingsRows.filter((row) => `${row.id}` === `${settingsFilter}`)

  const loadCategories = useCallback(async () => {
    if (!companyUrl) return
    setLoadingCategories(true)
    const response = await apiService.fetchFoodTemplateCategories(companyUrl)
    setLoadingCategories(false)

    if (!response.success) {
      toast({ title: 'Unable to load food categories', description: response.error, variant: 'destructive' })
      return
    }

    setCategories(Array.isArray(response.data) ? response.data : [])
  }, [companyUrl, toast])

  const loadSettings = useCallback(async () => {
    if (!companyUrl) return
    setLoadingSettings(true)
    const response = await apiService.fetchFoodTemplatesPerCategory(companyUrl)
    setLoadingSettings(false)

    if (!response.success) {
      toast({ title: 'Unable to load category settings', description: response.error, variant: 'destructive' })
      return
    }

    setSettingsRows(Array.isArray(response.data) ? response.data : [])
  }, [companyUrl, toast])

  const loadEditRows = useCallback(async () => {
    if (!companyUrl) return
    setLoadingEditList(true)
    const response = await apiService.fetchFoodTemplates(companyUrl, {
      orderByOn: 'name',
      orderBy: 'ASC',
      fromIndex: editPagination.pageIndex * editPagination.pageSize,
      searchtext: editSearch.trim(),
      limit: editPagination.pageSize,
    })
    setLoadingEditList(false)

    if (!response.success) {
      toast({ title: 'Unable to load food templates', description: response.error, variant: 'destructive' })
      return
    }

    setEditRows(Array.isArray(response.data?.rows) ? response.data.rows : [])
    setEditTotalCount(Number(response.data?.count) || 0)
    setEditSelectedIds([])
  }, [companyUrl, editPagination.pageIndex, editPagination.pageSize, editSearch, toast])

  const loadFoodDetail = async (templateId) => {
    if (!companyUrl || !templateId) return
    setSelectedTemplateId(templateId)
    setLoadingEditDetail(true)
    const response = await apiService.fetchFoodTemplateDetails(companyUrl, templateId)
    setLoadingEditDetail(false)

    if (!response.success) {
      toast({ title: 'Unable to load template details', description: response.error, variant: 'destructive' })
      return
    }

    setEditForm(normalizeFoodTemplate(response.data))
  }

  const handleFetchDetails = async (row) => {
    if (!companyUrl || !row.name) return
    if (expandedResultId === row.id) {
      setExpandedResultId(null)
      return
    }

    setExpandedResultId(row.id)
    if (detailedOptionsMap[row.id]) return

    setLoadingCreateSearch(true)
    const response = await apiService.fetchNutritionWithDetails(companyUrl, row.raw || row.name)
    setLoadingCreateSearch(false)

    if (!response.success) {
      toast({
        title: 'Unable to load food details',
        description: response.error,
        variant: 'destructive',
      })
      return
    }

    const options = response.data?.foods?.[0]?.alt_measures || []
    const mainFood = response.data?.foods?.[0]
    
    // Add main food as the first option if it's not in alt_measures
    const allOptions = mainFood ? [mainFood, ...options] : options

    setDetailedOptionsMap(prev => ({
      ...prev,
      [row.id]: allOptions
    }))
  }

  const handleNutritionPrefill = async (food) => {
    if (!food) return

    setCreateForm((prev) => ({
      ...FOOD_DEFAULT_FORM,
      name: prev.name,
      qty: toFormValue(food.serving_qty || food.qty || 1),
      unit: food.serving_unit || food.unit || '',
      calories: toFormValue(food.nf_calories || food.calories),
      protein: toFormValue(food.nf_protein || food.protein),
      carbs: toFormValue(food.nf_total_carbohydrate || food.carbs),
      fat: toFormValue(food.nf_total_fat || food.fat),
      sugars: toFormValue(food.nf_sugars || food.sugars),
      fiber: toFormValue(food.nf_dietary_fiber || food.fiber),
      calcium: toFormValue(food.nf_calcium || food.calcium),
      iron: toFormValue(food.nf_iron || food.iron),
      magnesium: toFormValue(food.nf_magnesium || food.magnesium),
      phosphorus: toFormValue(food.nf_p || food.phosphorus),
      potassium: toFormValue(food.nf_potassium || food.potassium),
      sodium: toFormValue(food.nf_sodium || food.sodium),
      zinc: toFormValue(food.nf_zinc || food.zinc),
      vitamin_a: toFormValue(food.nf_vitamin_a_dv || food.vitamin_a),
      vitamin_e: toFormValue(food.nf_vitamin_e || food.vitamin_e),
      vitamin_d: toFormValue(food.nf_vitamin_d || food.vitamin_d),
      vitamin_d3: toFormValue(food.nf_vitamin_d3 || food.vitamin_d3),
      vitamin_c: toFormValue(food.nf_vitamin_c || food.vitamin_c),
      thiamin: toFormValue(food.nf_thiamin || food.thiamin),
      riboflavin: toFormValue(food.nf_riboflavin || food.riboflavin),
      niacin: toFormValue(food.nf_niacin || food.niacin),
      pantothenic_acid: toFormValue(food.nf_pantothenic_acid || food.pantothenic_acid),
      vitamin_b6: toFormValue(food.nf_vitamin_b6 || food.vitamin_b6),
      folate: toFormValue(food.nf_folate || food.folate),
      vitamin_b12: toFormValue(food.nf_vitamin_b12 || food.vitamin_b12),
      leucine: toFormValue(food.nf_leucine || food.leucine),
      cholesterol: toFormValue(food.nf_cholesterol || food.cholesterol),
      fatty_acids: toFormValue(food.nf_fatty_acids || food.fatty_acids || food.nf_saturated_fat),
      dha: toFormValue(food.nf_dha || food.dha),
      epa: toFormValue(food.nf_epa || food.epa),
      ala: toFormValue(food.nf_ala || food.ala),
    }))

    setCreateSearch('')
    setCreateResults([])
    setExpandedResultId(null)
    setDetailedOptionsMap({})
  }

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  useEffect(() => {
    if (activeTab === 'settings') {
      loadSettings()
    }
  }, [activeTab, loadSettings])

  useEffect(() => {
    if (activeTab !== 'edit') return
    loadEditRows()
  }, [activeTab, loadEditRows])

  useEffect(() => {
    if (activeTab !== 'create') return
    if (!createSearch.trim()) {
      setCreateResults([])
      return
    }

    const timer = window.setTimeout(async () => {
      setLoadingCreateSearch(true)
      const response = await apiService.searchNewNutrition(companyUrl, createSearch.trim())
      setLoadingCreateSearch(false)

      if (!response.success) {
        toast({ title: 'Unable to search foods', description: response.error, variant: 'destructive' })
        return
      }

      const results = Array.isArray(response.data?.results) ? response.data.results : []
      const rows = results.map(buildNutritionSearchDraft)
      const uniqueRows = Array.from(new Map(rows.map((row) => [row.id, row])).values())
      setCreateResults(uniqueRows)
    }, 300)

    return () => window.clearTimeout(timer)
  }, [activeTab, companyUrl, createSearch, toast])

  const updateCreateField = (key, value) => {
    setCreateForm((current) => ({ ...current, [key]: value }))
  }

  const updateEditField = (key, value) => {
    setEditForm((current) => ({ ...current, [key]: value }))
  }

  const uploadFoodAssets = async (templateId, form) => {
    if (!form.imageFiles.length && !form.recipeFile) return true
    const uploadResponse = await apiService.uploadFoodTemplateAssets(companyUrl, templateId, {
      images: form.imageFiles,
      recipeFile: form.recipeFile,
    })

    if (!uploadResponse.success) {
      toast({
        title: 'Template saved',
        description: uploadResponse.error,
      })
      return false
    }

    return true
  }

  const createFoodCategoryIfNeeded = async (form) => {
    const newCategoryName = form.newCategoryName.trim()
    if (!newCategoryName) {
      return {
        categoryId: form.foodtemplatecategory_id ? Number(form.foodtemplatecategory_id) : null,
        categoryName: getCategoryName(categories, form.foodtemplatecategory_id),
      }
    }

    const createResponse = await apiService.createFoodTemplateCategory(companyUrl)
    if (!createResponse.success) {
      throw new Error(createResponse.error)
    }

    const categoryId = extractId(createResponse.data)
    if (!categoryId) {
      throw new Error('New category could not be created right now.')
    }

    const updateResponse = await apiService.updateFoodTemplateCategory(companyUrl, categoryId, {
      name: newCategoryName,
      parent_id: form.newCategoryParentId ? Number(form.newCategoryParentId) : null,
    })

    if (!updateResponse.success) {
      throw new Error(updateResponse.error)
    }

    return {
      categoryId,
      categoryName: newCategoryName,
    }
  }

  const handleCreateFood = async () => {
    if (!createForm.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' })
      return
    }
    if (!createForm.foodtemplatecategory_id) {
      toast({ title: 'Food Category is required', variant: 'destructive' })
      return
    }
    if (!createForm.newCategoryParentId) {
      toast({ title: 'Parent Category is required', variant: 'destructive' })
      return
    }

    setSavingCreate(true)

    try {
      const categoryResult = await createFoodCategoryIfNeeded(createForm)
      const response = await apiService.createFoodTemplate(companyUrl, buildFoodPayload(createForm, categoryResult.categoryId))

      if (!response.success) {
        throw new Error(response.error)
      }

      const templateId = extractId(response.data)
      if (templateId) {
        await uploadFoodAssets(templateId, createForm)
      }

      toast({ title: 'Food template created' })
      setCreateForm({ ...FOOD_DEFAULT_FORM })
      setCreateSearch('')
      setCreateResults([])
      loadCategories()
      loadSettings()
      loadEditRows()
      onTabChange('edit')
    } catch (error) {
      toast({ title: 'Unable to create food template', description: error.message, variant: 'destructive' })
    } finally {
      setSavingCreate(false)
    }
  }

  const handleSaveFoodEdit = async () => {
    if (!selectedTemplateId) return
    if (!editForm.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' })
      return
    }

    setSavingEdit(true)
    const response = await apiService.updateFoodTemplate(
      companyUrl,
      selectedTemplateId,
      buildFoodPayload(editForm, Number(editForm.foodtemplatecategory_id))
    )

    if (!response.success) {
      setSavingEdit(false)
      toast({ title: 'Unable to update template', description: response.error, variant: 'destructive' })
      return
    }

    await uploadFoodAssets(selectedTemplateId, editForm)
    setSavingEdit(false)
    toast({ title: 'Food template updated' })
    loadEditRows()
    loadSettings()
    loadFoodDetail(selectedTemplateId)
  }

  const handleDeleteFoodRows = () => {
    if (!editSelectedIds.length) return
    setDeleteModal({
      open: true,
      type: 'templates',
      title: 'Delete Food Templates',
      description: `Are you sure you want to delete ${editSelectedIds.length} selected food template(s)? This action cannot be undone.`,
    })
  }

  const performDeleteTemplates = async () => {
    setDeletingRows(true)
    const response = await apiService.deleteFoodTemplates(companyUrl, editSelectedIds)
    setDeletingRows(false)

    if (!response.success) {
      toast({ title: 'Unable to delete templates', description: response.error, variant: 'destructive' })
      return
    }

    toast({ title: 'Selected food templates deleted' })
    setSelectedTemplateId(null)
    setEditForm({ ...FOOD_DEFAULT_FORM })
    setDeleteModal((prev) => ({ ...prev, open: false }))
    loadEditRows()
    loadSettings()
  }

  const handleFoodParentChange = async (categoryId, value) => {
    setSavingSettings(true)
    const response = await apiService.updateFoodTemplateCategory(companyUrl, categoryId, {
      parent_id: value ? Number(value) : null,
    })
    setSavingSettings(false)

    if (!response.success) {
      toast({ title: 'Unable to update category', description: response.error, variant: 'destructive' })
      return
    }

    loadCategories()
    loadSettings()
  }

  const handleCreateFoodCategory = async () => {
    if (!settingsNewCategoryName.trim()) {
      toast({ title: 'Enter a category name first', variant: 'destructive' })
      return
    }

    setSavingSettings(true)
    const createResponse = await apiService.createFoodTemplateCategory(companyUrl)

    if (!createResponse.success) {
      setSavingSettings(false)
      toast({ title: 'Unable to create category', description: createResponse.error, variant: 'destructive' })
      return
    }

    const categoryId = extractId(createResponse.data)
    if (!categoryId) {
      setSavingSettings(false)
      toast({ title: 'Unable to create category', description: 'The category id was not returned.', variant: 'destructive' })
      return
    }

    const updateResponse = await apiService.updateFoodTemplateCategory(companyUrl, categoryId, {
      name: settingsNewCategoryName.trim(),
      parent_id: null,
    })
    setSavingSettings(false)

    if (!updateResponse.success) {
      toast({ title: 'Unable to update category', description: updateResponse.error, variant: 'destructive' })
      return
    }

    setSettingsNewCategoryName('')
    toast({ title: 'Food category created' })
    loadCategories()
    await loadSettings()

    // Scroll the entire page to the bottom
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
    }, 100)
  }

  const handleDeleteFoodCategory = (categoryId) => {
    setDeleteModal({
      open: true,
      type: 'category',
      id: categoryId,
      title: 'Delete Food Category',
      description: 'Are you sure you want to delete this food category? This action cannot be undone.',
    })
  }

  const performDeleteCategory = async () => {
    if (!deleteModal.id) return
    setSavingSettings(true)
    const response = await apiService.deleteFoodTemplateCategory(companyUrl, deleteModal.id)
    setSavingSettings(false)

    if (!response.success) {
      toast({ title: 'Unable to delete category', description: response.error, variant: 'destructive' })
      return
    }

    toast({ title: 'Food category deleted' })
    setDeleteModal((prev) => ({ ...prev, open: false, id: null }))
    loadCategories()
    loadSettings()
  }

  return (
    <div className="uploader-page">
      <PageHero
        title="Food Templates"
        description="Create new food templates, update the catalog with pagination and delete actions, and organize categories in one place."
        onBack={onBack}
      />

      <WorkspaceTabs tabs={FOOD_TABS} activeTab={activeTab} onChange={onTabChange} />

      <div className="uploader-surface space-y-6">
        {activeTab === 'create' ? (
          <FoodTemplateForm
            form={createForm}
            categories={categories}
            searchTerm={createSearch}
            onSearchTermChange={setCreateSearch}
            searchResults={createResults}
            expandedResultId={expandedResultId}
            onFetchDetails={handleFetchDetails}
            detailedOptionsMap={detailedOptionsMap}
            onPrefill={handleNutritionPrefill}
            onFieldChange={updateCreateField}
            onImageFilesChange={(event) =>
              updateCreateField('imageFiles', Array.from(event.target.files || []))
            }
            onRecipeFileChange={(event) => updateCreateField('recipeFile', event.target.files?.[0] || null)}
            onSubmit={handleCreateFood}
            submitLabel={savingCreate ? 'Saving...' : 'Submit'}
            isSubmitting={savingCreate || loadingCreateSearch || loadingCategories}
            mode="create"
          />
        ) : null}

        {activeTab === 'edit' ? (
          <div className="grid gap-5 xl:grid-cols-[minmax(320px,0.85fr),minmax(0,1.5fr)]">
          <Card className="min-w-0 border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Food Catalog</CardTitle>
              <CardDescription className="text-xs">Browse templates, page through results, and delete selected rows.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-col gap-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={editSearch}
                    onChange={(event) => {
                      setEditPagination((current) => ({ ...current, pageIndex: 0 }))
                      setEditSearch(event.target.value)
                    }}
                    placeholder="Search food templates"
                    className="pl-9 h-9 text-sm"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <select
                    className="flex h-8 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 focus:border-fitness-primary focus:outline-none"
                    value={editPagination.pageSize}
                    onChange={(event) =>
                      setEditPagination({ pageIndex: 0, pageSize: Number(event.target.value) })
                    }
                  >
                    {[10, 25, 50].map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </select>

                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={handleDeleteFoodRows}
                    disabled={!editSelectedIds.length || deletingRows}
                  >
                    {deletingRows ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                    Delete Selected
                  </Button>
                </div>
              </div>

              {loadingEditList ? (
                <EmptyState message="Loading food templates..." />
              ) : editRows.length ? (
                <>
                  <div className="overflow-auto rounded-2xl border border-slate-200 max-h-[380px]">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-2.5">
                            <input
                              type="checkbox"
                              checked={editRows.length > 0 && editRows.every((row) => editSelectedIds.includes(row.id))}
                              onChange={(event) =>
                                setEditSelectedIds(event.target.checked ? editRows.map((row) => row.id) : [])
                              }
                            />
                          </th>
                          <th className="px-3 py-2.5">Name</th>
                          <th className="px-3 py-2.5">Category</th>
                          <th className="px-3 py-2.5">Cal</th>
                          <th className="px-3 py-2.5">Pro</th>
                          <th className="px-3 py-2.5">Carbs</th>
                          <th className="px-3 py-2.5">Fat</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {editRows.map((row) => (
                          <tr
                            key={row.id}
                            className={cn(
                              'cursor-pointer transition hover:bg-slate-50',
                              Number(selectedTemplateId) === Number(row.id) && 'bg-fitness-primary/8 ring-1 ring-inset ring-fitness-primary/20'
                            )}
                            onClick={() => loadFoodDetail(row.id)}
                          >
                            <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={editSelectedIds.includes(row.id)}
                                onChange={() =>
                                  setEditSelectedIds((current) =>
                                    current.includes(row.id)
                                      ? current.filter((id) => id !== row.id)
                                      : [...current, row.id]
                                  )
                                }
                              />
                            </td>
                            <td className="px-3 py-2 text-xs font-medium text-slate-900 max-w-[140px] truncate">{row.name}</td>
                            <td className="px-3 py-2 text-xs text-slate-500 max-w-[100px] truncate">{row.foodtemplatecategory?.name || '—'}</td>
                            <td className="px-3 py-2 text-xs text-slate-600">{formatNumberSmart(row.calories)}</td>
                            <td className="px-3 py-2 text-xs text-slate-600">{formatNumberSmart(row.protein)}</td>
                            <td className="px-3 py-2 text-xs text-slate-600">{formatNumberSmart(row.carbs)}</td>
                            <td className="px-3 py-2 text-xs text-slate-600">{formatNumberSmart(row.fat)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                    <span>
                      <span className="font-medium text-slate-800">
                        {editRows.length ? editPagination.pageIndex * editPagination.pageSize + 1 : 0}–
                        {editPagination.pageIndex * editPagination.pageSize + editRows.length}
                      </span>{' '}
                      of {editTotalCount}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                          setEditPagination((current) => ({ ...current, pageIndex: Math.max(0, current.pageIndex - 1) }))
                        }
                        disabled={editPagination.pageIndex === 0}
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </Button>
                      <span className="min-w-[70px] text-center text-xs">
                        {Math.min(editPagination.pageIndex + 1, editPageCount)} / {editPageCount}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                          setEditPagination((current) => ({
                            ...current,
                            pageIndex: Math.min(editPageCount - 1, current.pageIndex + 1),
                          }))
                        }
                        disabled={editPagination.pageIndex >= editPageCount - 1}
                      >
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <EmptyState message="No food templates matched the current search." />
              )}
            </CardContent>
          </Card>

          <div className="space-y-6">
            {selectedTemplateId ? (
              <>

                <FoodTemplateForm
                  form={editForm}
                  categories={categories}
                  searchTerm=""
                  onSearchTermChange={() => {}}
                  searchResults={[]}
                  onPrefill={() => {}}
                  onFieldChange={updateEditField}
                  onImageFilesChange={(event) =>
                    updateEditField('imageFiles', Array.from(event.target.files || []))
                  }
                  onRecipeFileChange={(event) => updateEditField('recipeFile', event.target.files?.[0] || null)}
                  onSubmit={handleSaveFoodEdit}
                  onCancel={() => {
                    setSelectedTemplateId(null)
                    setEditForm({ ...FOOD_DEFAULT_FORM })
                  }}
                  submitLabel={savingEdit ? 'Saving...' : 'Save Changes'}
                  isSubmitting={savingEdit || loadingEditDetail}
                  mode="edit"
                />
              </>
            ) : (
              <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-center p-6">
                <Pencil className="h-8 w-8 text-slate-300" />
                <p className="text-sm font-medium text-slate-600">Select a row to edit</p>
                <p className="text-xs text-slate-400">Click any food template in the catalog to load its details here.</p>
              </div>
            )}
          </div>
          </div>
        ) : null}

        {activeTab === 'settings' ? (
          <Card className="min-w-0 border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-1">
                <CardTitle className="text-xl font-bold text-slate-900">Category Settings</CardTitle>
                <CardDescription className="text-slate-500">Organize parent categories and review the templates assigned to each category.</CardDescription>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">Filter:</span>
                  <select
                    className="flex h-10 min-w-[160px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                    value={settingsFilter}
                    onChange={(event) => setSettingsFilter(event.target.value)}
                  >
                    <option value="all">All Categories</option>
                    {settingsRows.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2">
                  <Input
                    value={settingsNewCategoryName}
                    onChange={(event) => setSettingsNewCategoryName(event.target.value)}
                    placeholder="New category name"
                    className="min-w-[200px]"
                  />
                  <Button type="button" onClick={handleCreateFoodCategory} disabled={savingSettings}>
                    {savingSettings ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                    Add Category
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingSettings ? (
              <EmptyState message="Loading category settings..." />
            ) : visibleSettingsRows.length ? (
              <div ref={settingsTableRef} className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Parent Category</th>
                      <th className="px-5 py-3">Food Templates</th>
                      <th className="px-5 py-3 text-right">Delete</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {visibleSettingsRows.map((row) => (
                      <tr key={row.id}>
                        <td className="px-5 py-4 align-top font-medium text-slate-900">{row.name}</td>
                        <td className="px-5 py-4 align-top">
                          <select
                            className="flex h-10 min-w-[220px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                            value={row.parent_id || ''}
                            onChange={(event) => handleFoodParentChange(row.id, event.target.value)}
                          >
                            <option value="">No parent category</option>
                            {settingsRows
                              .filter((candidate) => candidate.id !== row.id)
                              .map((candidate) => (
                                <option key={candidate.id} value={candidate.id}>
                                  {candidate.name}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td className="px-5 py-4">
                          {row.foodtemplates?.length ? (
                            <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto pr-2 custom-scrollbar">
                              {row.foodtemplates.map((template) => (
                                <Badge key={template.id} className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                                  {template.name}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-500">No templates assigned</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right align-top">
                          {!row.foodtemplates?.length ? (
                            <Button type="button" variant="ghost" size="icon" onClick={() => handleDeleteFoodCategory(row.id)}>
                              <Trash2 className="h-4 w-4 text-slate-500" />
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState message="No categories matched the current filter." />
            )}
          </CardContent>
          </Card>
        ) : null}
      </div>

      <ConfirmDeleteModal
        open={deleteModal.open}
        onOpenChange={(open) => setDeleteModal((prev) => ({ ...prev, open }))}
        onConfirm={deleteModal.type === 'templates' ? performDeleteTemplates : performDeleteCategory}
        title={deleteModal.title}
        description={deleteModal.description}
        isDeleting={deleteModal.type === 'templates' ? deletingRows : savingSettings}
      />
    </div>
  )
}

function ExerciseTemplatesWorkspace({ companyUrl, activeTab, onTabChange, onBack }) {
  const { toast } = useToast()
  const [categoryTree, setCategoryTree] = useState([])
  const [allCategoryRows, setAllCategoryRows] = useState([])
  const [categoryRows, setCategoryRows] = useState([])
  const [createForm, setCreateForm] = useState({ ...EXERCISE_DEFAULT_FORM })
  const [editSearch, setEditSearch] = useState('')
  const [editPagination, setEditPagination] = useState({ ...EMPTY_EDIT_PAGINATION })
  const [editRows, setEditRows] = useState([])
  const [editTotalCount, setEditTotalCount] = useState(0)
  const [selectedIds, setSelectedIds] = useState([])
  const [selectedTemplateId, setSelectedTemplateId] = useState(null)
  const [editDraft, setEditDraft] = useState({ ...EXERCISE_DEFAULT_FORM })
  const [editCatalogCollapsed, setEditCatalogCollapsed] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingCategoryRows, setLoadingCategoryRows] = useState(false)
  const [loadingEditRows, setLoadingEditRows] = useState(false)
  const [savingCreate, setSavingCreate] = useState(false)
  const [savingEdit, setSavingEdit] = useState(false)
  const [deletingEditRows, setDeletingEditRows] = useState(false)
  const [savingCategoryChange, setSavingCategoryChange] = useState(false)
  const [deleteExercisesDialogOpen, setDeleteExercisesDialogOpen] = useState(false)

  const flatCategories = flattenExerciseCategories(categoryTree)
  const topLevelCategories = getTopLevelExerciseCategories(categoryTree)
  const topLevelCategoryRows = allCategoryRows.filter((row) => !row.parent_id)
  const editPageCount = Math.max(1, Math.ceil(editTotalCount / editPagination.pageSize))
  const visibleCategoryRows = categoryRows

  const loadExerciseCategoryTree = useCallback(async () => {
    if (!companyUrl) return
    setLoadingCategories(true)
    const response = await apiService.fetchExerciseTemplateCategories(companyUrl)
    setLoadingCategories(false)

    if (!response.success) {
      toast({ title: 'Unable to load exercise categories', description: response.error, variant: 'destructive' })
      return
    }

    setCategoryTree(Array.isArray(response.data) ? response.data : [])
  }, [companyUrl, toast])

  const loadExerciseCategoryRows = useCallback(async (selectedCategoryId = 'all') => {
    if (!companyUrl) return
    setLoadingCategoryRows(true)
    const response = await apiService.fetchExerciseTemplatesPerCategory(
      companyUrl,
      selectedCategoryId === 'all' ? null : selectedCategoryId
    )
    setLoadingCategoryRows(false)

    if (!response.success) {
      toast({ title: 'Unable to load exercise categories', description: response.error, variant: 'destructive' })
      return
    }

    if (Array.isArray(response.data)) {
      setCategoryRows(response.data)
      if (selectedCategoryId === 'all') {
        setAllCategoryRows(response.data)
      }
      return
    }

    setCategoryRows(response.data ? [response.data] : [])
  }, [companyUrl, toast])

  const loadExerciseEditRows = useCallback(async () => {
    if (!companyUrl) return
    setLoadingEditRows(true)
    const response = await apiService.fetchExerciseTemplates(companyUrl, {
      orderByOn: 'name',
      orderBy: 'ASC',
      fromIndex: editPagination.pageIndex * editPagination.pageSize,
      searchtext: editSearch.trim(),
      limit: editPagination.pageSize,
    })
    setLoadingEditRows(false)

    if (!response.success) {
      toast({ title: 'Unable to load exercise templates', description: response.error, variant: 'destructive' })
      return
    }

    setEditRows(Array.isArray(response.data?.rows) ? response.data.rows : [])
    setEditTotalCount(Number(response.data?.count) || 0)
    setSelectedIds([])
  }, [companyUrl, editPagination.pageIndex, editPagination.pageSize, editSearch, toast])

  useEffect(() => {
    loadExerciseCategoryTree()
    loadExerciseCategoryRows()
  }, [loadExerciseCategoryRows, loadExerciseCategoryTree])

  const handleExerciseCategoryFilterChange = async (value) => {
    setCategoryFilter(value)
    await loadExerciseCategoryRows(value)
  }

  useEffect(() => {
    if (activeTab === 'edit') {
      loadExerciseEditRows()
    }
  }, [activeTab, loadExerciseEditRows])

  const updateCreateField = (key, value) => {
    setCreateForm((current) => {
      const next = { ...current, [key]: value }

      if (key === 'exercisetemplatecategory_id' && value !== '') {
        next.newCategoryParentId = ''
        next.newCategoryName = ''
      } else if (key === 'newCategoryParentId' && value !== '') {
        next.exercisetemplatecategory_id = ''
      } else if (key === 'newCategoryName' && value.trim() !== '') {
        next.exercisetemplatecategory_id = ''
      }

      return next
    })
  }

  const updateEditField = (key, value) => {
    setEditDraft((current) => ({ ...current, [key]: value }))
  }

  const uploadExerciseAssets = async (templateId, form) => {
    if (!form.imageFiles.length) return true
    const response = await apiService.uploadExerciseTemplateAssets(companyUrl, templateId, {
      images: form.imageFiles,
    })

    if (!response.success) {
      toast({ title: 'Exercise saved', description: response.error })
      return false
    }

    return true
  }

  const handleCreateExercise = async () => {
    if (!createForm.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' })
      return
    }

    const hasExerciseCategory = Boolean(createForm.exercisetemplatecategory_id)
    const hasParentCategory = Boolean(createForm.newCategoryParentId)
    const hasNewCategoryName = Boolean(createForm.newCategoryName.trim())

    if (hasExerciseCategory && (hasParentCategory || hasNewCategoryName)) {
      toast({
        title: 'Use either Exercise Category or New Exercise Category',
        variant: 'destructive',
      })
      return
    }

    if (!hasExerciseCategory && !hasNewCategoryName) {
      toast({
        title: 'Select Exercise Category or enter New Exercise Category before submitting',
        variant: 'destructive',
      })
      return
    }

    if (hasParentCategory && !hasNewCategoryName) {
      toast({ title: 'New Exercise Category is required', variant: 'destructive' })
      return
    }

    setSavingCreate(true)
    try {
      const response = await apiService.createExerciseTemplate(
        companyUrl,
        buildExerciseCreatePayload(createForm)
      )

      if (!response.success) {
        toast({ title: 'Unable to create exercise template', description: response.error, variant: 'destructive' })
        return
      }

      const templateId = extractId(response.data)
      if (templateId) {
        await uploadExerciseAssets(templateId, createForm)
      }

      setCreateForm({ ...EXERCISE_DEFAULT_FORM })
      toast({ title: 'Exercise template created' })
      loadExerciseCategoryTree()
      loadExerciseCategoryRows()
      loadExerciseEditRows()
      onTabChange('edit')
    } finally {
      setSavingCreate(false)
    }
  }

  const loadExerciseDetail = (template) => {
    if (!template?.id) return
    setSelectedTemplateId(template.id)
    setEditDraft(normalizeExerciseTemplate(template, topLevelCategories, categoryTree))
    setEditCatalogCollapsed(true)
  }

  const handleSaveExerciseEdit = async () => {
    if (!selectedTemplateId) return
    if (!editDraft.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' })
      return
    }

    setSavingEdit(true)
    try {
      const response = await apiService.updateExerciseTemplate(
        companyUrl,
        selectedTemplateId,
        buildExerciseUpdatePayload(editDraft)
      )

      if (!response.success) {
        toast({ title: 'Unable to update exercise template', description: response.error, variant: 'destructive' })
        return
      }

      await uploadExerciseAssets(selectedTemplateId, editDraft)
      toast({ title: 'Exercise template updated' })
      setSelectedTemplateId(null)
      setEditDraft({ ...EXERCISE_DEFAULT_FORM })
      setEditCatalogCollapsed(false)
      loadExerciseEditRows()
      loadExerciseCategoryRows(categoryFilter)
    } finally {
      setSavingEdit(false)
    }
  }

  const handleDeleteExercises = async () => {
    if (!selectedIds.length) return
    setDeletingEditRows(true)
    const response = await apiService.deleteExerciseTemplates(companyUrl, selectedIds)
    setDeletingEditRows(false)

    if (!response.success) {
      toast({ title: 'Unable to delete exercise templates', description: response.error, variant: 'destructive' })
      return
    }

    toast({ title: 'Selected exercise templates deleted' })
    setDeleteExercisesDialogOpen(false)
    setSelectedTemplateId(null)
    setEditDraft({ ...EXERCISE_DEFAULT_FORM })
    setEditCatalogCollapsed(false)
    loadExerciseEditRows()
    loadExerciseCategoryRows(categoryFilter)
  }

  const handleExerciseCategoryParentChange = async (categoryId, value) => {
    setSavingCategoryChange(true)
    const response = await apiService.updateExerciseTemplateCategory(companyUrl, categoryId, {
      parent_id: value ? Number(value) : null,
    })
    setSavingCategoryChange(false)

    if (!response.success) {
      toast({ title: 'Unable to update category', description: response.error, variant: 'destructive' })
      return
    }

    loadExerciseCategoryTree()
    loadExerciseCategoryRows(categoryFilter)
  }

  const handleDeleteExerciseCategory = async (categoryId) => {
    setSavingCategoryChange(true)
    const response = await apiService.deleteExerciseTemplateCategory(companyUrl, categoryId)
    setSavingCategoryChange(false)

    if (!response.success) {
      toast({ title: 'Unable to delete category', description: response.error, variant: 'destructive' })
      return
    }

    toast({ title: 'Exercise category deleted' })
    loadExerciseCategoryTree()
    loadExerciseCategoryRows(categoryFilter)
  }

  return (
    <div className="uploader-page">
      <PageHero
        title="Exercise Templates"
        description="Create new exercise templates, edit descriptions and category assignments, and keep the category layout organized."
        onBack={onBack}
      />

      <WorkspaceTabs tabs={EXERCISE_TABS} activeTab={activeTab} onChange={onTabChange} />

      <div className="uploader-surface space-y-6">
        {activeTab === 'create' ? (
          <ExerciseTemplateForm
            form={createForm}
            categories={topLevelCategories}
            parentCategories={topLevelCategories}
            onFieldChange={updateCreateField}
            onImageFilesChange={(event) =>
              updateCreateField('imageFiles', Array.from(event.target.files || []))
            }
            onSubmit={handleCreateExercise}
            onCancel={() => setCreateForm({ ...EXERCISE_DEFAULT_FORM })}
            submitLabel={savingCreate ? 'Saving...' : 'Submit'}
            isSubmitting={savingCreate || loadingCategories}
            mode="create"
          />
        ) : null}

        {activeTab === 'edit' ? (
          <div className={cn('grid gap-6', selectedTemplateId && editCatalogCollapsed ? 'xl:grid-cols-[88px,minmax(0,1fr)]' : 'grid-cols-1')}>
          {selectedTemplateId && editCatalogCollapsed ? (
          <div className="space-y-6">
            <CollapsedCatalogCard label={editDraft?.name} onExpand={() => setEditCatalogCollapsed(false)} title="Exercise" />
          </div>
          ) : (
          <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <CardTitle className="text-lg">Exercise Catalog</CardTitle>
                  <CardDescription>Browse templates, page through results, and delete selected rows.</CardDescription>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                  <div className="relative sm:min-w-[240px]">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={editSearch}
                      onChange={(event) => {
                        setEditPagination((current) => ({ ...current, pageIndex: 0 }))
                        setEditSearch(event.target.value)
                      }}
                      placeholder="Search"
                      className="w-full pl-9"
                    />
                  </div>

                  <Button
                    type="button"
                    variant="destructive"
                    disabled={!selectedIds.length || deletingEditRows}
                    onClick={() => setDeleteExercisesDialogOpen(true)}
                  >
                    {deletingEditRows ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                    Delete Selected
                  </Button>
                  {selectedTemplateId ? <Button type="button" variant="outline" onClick={() => setEditCatalogCollapsed(true)}>Resume Editor</Button> : null}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingEditRows ? (
                <EmptyState message="Loading exercise templates..." />
              ) : editRows.length ? (
                <>
                  <div className="overflow-x-auto rounded-2xl border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={editRows.length > 0 && editRows.every((row) => selectedIds.includes(row.id))}
                              onChange={(event) =>
                                setSelectedIds(event.target.checked ? editRows.map((row) => row.id) : [])
                              }
                            />
                          </th>
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Category</th>
                          <th className="px-4 py-3">Video URL</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {editRows.map((row) => {
                          const rowCategoryName =
                            row.exercisetemplatecategory?.name || getCategoryName(flatCategories, row.exercisetemplatecategory_id)

                          return (
                            <tr
                              key={row.id}
                              className={cn(
                                'cursor-pointer transition hover:bg-slate-50',
                                Number(selectedTemplateId) === Number(row.id) && 'bg-fitness-primary/5'
                              )}
                              onClick={() => loadExerciseDetail(row)}
                            >
                              <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedIds.includes(row.id)}
                                  onChange={() =>
                                    setSelectedIds((current) =>
                                      current.includes(row.id)
                                        ? current.filter((id) => id !== row.id)
                                        : [...current, row.id]
                                    )
                                  }
                                />
                              </td>
                              <td className="px-4 py-3 font-medium text-slate-900">{row.name}</td>
                              <td className="px-4 py-3 text-slate-600">{rowCategoryName || 'Uncategorized'}</td>
                              <td className="px-4 py-3 text-slate-600">
                                {row.video_url ? (
                                  <span className="text-fitness-primary truncate max-w-[150px] inline-block">
                                    {row.video_url}
                                  </span>
                                ) : (
                                  'None'
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                      <select
                        className="flex h-9 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                        value={editPagination.pageSize}
                        onChange={(event) =>
                          setEditPagination({ pageIndex: 0, pageSize: Number(event.target.value) })
                        }
                      >
                        {[10, 25, 50].map((size) => (
                          <option key={size} value={size}>
                            {size} / page
                          </option>
                        ))}
                      </select>
                    </div>

                    <p>
                      {editRows.length ? editPagination.pageIndex * editPagination.pageSize + 1 : 0}-
                      {editPagination.pageIndex * editPagination.pageSize + editRows.length} of {editTotalCount}
                    </p>

                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setEditPagination((current) => ({ ...current, pageIndex: Math.max(0, current.pageIndex - 1) }))
                        }
                        disabled={editPagination.pageIndex === 0}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="min-w-[88px] text-center">
                        Page {Math.min(editPagination.pageIndex + 1, editPageCount)} of {editPageCount}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setEditPagination((current) => ({
                            ...current,
                            pageIndex: Math.min(editPageCount - 1, current.pageIndex + 1),
                          }))
                        }
                        disabled={editPagination.pageIndex >= editPageCount - 1}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <EmptyState message="No exercise templates matched the current search." />
              )}
            </CardContent>
          </Card>
          </div>
          )}

          {selectedTemplateId && editCatalogCollapsed ? (
          <div className="min-w-0 space-y-6">
              <>

                <ExerciseTemplateForm
                  form={editDraft}
                  categories={topLevelCategories}
                  parentCategories={topLevelCategories}
                  onFieldChange={updateEditField}
                  onImageFilesChange={(event) =>
                    updateEditField('imageFiles', Array.from(event.target.files || []))
                  }
                  onSubmit={handleSaveExerciseEdit}
                  onCancel={() => {
                    setSelectedTemplateId(null)
                    setEditDraft({ ...EXERCISE_DEFAULT_FORM })
                    setEditCatalogCollapsed(false)
                  }}
                  submitLabel={savingEdit ? 'Saving...' : 'Save Changes'}
                  isSubmitting={savingEdit}
                  mode="edit"
                />
              </>
          </div>
          ) : null}
          </div>
        ) : null}

        {activeTab === 'categories' ? (
          <Card className="min-w-0 border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle className="text-lg">Exercise Categories</CardTitle>
                <CardDescription>Review the category layout, parent assignments, and the templates under each category.</CardDescription>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-600">View category layout</span>
                <select
                  className="flex h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                  value={categoryFilter}
                  onChange={(event) => handleExerciseCategoryFilterChange(event.target.value)}
                >
                  <option value="all">All Categories</option>
                  {topLevelCategoryRows.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingCategoryRows ? (
              <EmptyState message="Loading exercise categories..." />
            ) : visibleCategoryRows.length ? (
              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Parent Category</th>
                      <th className="px-5 py-3">Exercise</th>
                      <th className="px-5 py-3 text-right">Delete</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {visibleCategoryRows.map((row) => (
                      <tr key={row.id} className="h-[152px]">
                        <td className="px-5 py-4 align-top font-medium text-slate-900">{row.name}</td>
                        <td className="px-5 py-4 align-top">
                          <select
                            className="flex h-10 min-w-[220px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                            value={row.parent_id || ''}
                            onChange={(event) => handleExerciseCategoryParentChange(row.id, event.target.value)}
                          >
                            <option value="">No Parent Category</option>
                            {topLevelCategoryRows
                              .filter((candidate) => candidate.id !== row.id)
                              .map((candidate) => (
                                <option key={candidate.id} value={candidate.id}>
                                  {candidate.name}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td className="px-5 py-4">
                          {row.exercisetemplates?.length ? (
                            <div className="flex flex-wrap gap-2">
                              {row.exercisetemplates.map((exercise) => (
                                <Badge key={exercise.id} className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                                  {exercise.name}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-500">No exercises assigned</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right align-top">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={savingCategoryChange}
                            onClick={() => handleDeleteExerciseCategory(row.id)}
                          >
                            <Trash2 className="h-4 w-4 text-slate-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState message="No exercise categories matched the current filter." />
            )}
          </CardContent>
          </Card>
        ) : null}
      </div>

      <ConfirmDeleteModal
        open={deleteExercisesDialogOpen}
        onOpenChange={setDeleteExercisesDialogOpen}
        onConfirm={handleDeleteExercises}
        title="Delete selected exercise templates?"
        description={`This will permanently delete ${selectedIds.length} exercise template${selectedIds.length === 1 ? '' : 's'}.`}
        isDeleting={deletingEditRows}
      />
    </div>
  )
}

function MealTemplatesWorkspace({ companyUrl, activeTab, onTabChange, onBack }) {
  const { toast } = useToast()
  const [categories, setCategories] = useState([])
  const [allCategoryRows, setAllCategoryRows] = useState([])
  const [categoryRows, setCategoryRows] = useState([])
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [createForm, setCreateForm] = useState({ ...MEAL_DEFAULT_FORM })
  const [createActiveFoodId, setCreateActiveFoodId] = useState(null)
  const [mealRows, setMealRows] = useState([])
  const [mealSearch, setMealSearch] = useState('')
  const [mealPagination, setMealPagination] = useState({ ...EMPTY_EDIT_PAGINATION })
  const [mealTotalCount, setMealTotalCount] = useState(0)
  const [selectedMealIds, setSelectedMealIds] = useState([])
  const [expandedMealId, setExpandedMealId] = useState(null)
  const [expandedMealBaseline, setExpandedMealBaseline] = useState({ ...MEAL_DEFAULT_FORM })
  const [editCatalogCollapsed, setEditCatalogCollapsed] = useState(false)
  const [editForm, setEditForm] = useState({ ...MEAL_DEFAULT_FORM })
  const [editActiveFoodId, setEditActiveFoodId] = useState(null)
  const [selectedEditFoodIds, setSelectedEditFoodIds] = useState([])
  const [expandedMealFoodId, setExpandedMealFoodId] = useState(null)
  const [mealFoodDetailRows, setMealFoodDetailRows] = useState({})
  const [nutritionSearch, setNutritionSearch] = useState('')
  const [nutritionPagination, setNutritionPagination] = useState({ ...EMPTY_SEARCH_PAGINATION })
  const [nutritionResults, setNutritionResults] = useState([])
  const [nutritionTotalCount, setNutritionTotalCount] = useState(0)
  const [expandedNutritionResultId, setExpandedNutritionResultId] = useState(null)
  const [nutritionDetailMap, setNutritionDetailMap] = useState({})
  const [foodTemplateRows, setFoodTemplateRows] = useState([])
  const [foodTemplatePagination, setFoodTemplatePagination] = useState({ ...EMPTY_EDIT_PAGINATION })
  const [foodTemplateTotalCount, setFoodTemplateTotalCount] = useState(0)
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingCategoryRows, setLoadingCategoryRows] = useState(false)
  const [loadingFoodTemplates, setLoadingFoodTemplates] = useState(false)
  const [loadingMealRows, setLoadingMealRows] = useState(false)
  const [loadingExpandedMeal, setLoadingExpandedMeal] = useState(false)
  const [loadingNutrition, setLoadingNutrition] = useState(false)
  const [nutritionDetailLoadingId, setNutritionDetailLoadingId] = useState(null)
  const [mealFoodDetailLoadingId, setMealFoodDetailLoadingId] = useState(null)
  const [savingDraft, setSavingDraft] = useState(false)
  const [deletingRows, setDeletingRows] = useState(false)
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    type: null,
    payload: null,
    title: '',
    description: '',
  })

  const visibleNutritionRows = nutritionResults.slice(
    nutritionPagination.pageIndex * nutritionPagination.pageSize,
    nutritionPagination.pageIndex * nutritionPagination.pageSize + nutritionPagination.pageSize
  )

  const topLevelCategoryRows = allCategoryRows.filter((row) => !row.parent_id)
  const visibleCategoryRows = categoryRows

  const createActiveFood = (createForm.mealFoods || []).find((food) => food.id === createActiveFoodId) || null
  const editActiveFood = (editForm.mealFoods || []).find((food) => food.id === editActiveFoodId) || null

  const loadMealCategories = useCallback(async () => {
    if (!companyUrl) return
    setLoadingCategories(true)
    const response = await apiService.fetchMealTemplateCategories(companyUrl)
    setLoadingCategories(false)

    if (!response.success) {
      toast({ title: 'Unable to load meal categories', description: response.error, variant: 'destructive' })
      return
    }

    setCategories(Array.isArray(response.data) ? response.data : [])
  }, [companyUrl, toast])

  const loadMealCategoryRows = useCallback(async (selectedCategoryId = 'all') => {
    if (!companyUrl) return
    setLoadingCategoryRows(true)
    const response = await apiService.fetchMealTemplatesPerCategory(
      companyUrl,
      selectedCategoryId === 'all' ? null : selectedCategoryId
    )
    setLoadingCategoryRows(false)

    if (!response.success) {
      toast({ title: 'Unable to load category layout', description: response.error, variant: 'destructive' })
      return
    }

    if (Array.isArray(response.data)) {
      setCategoryRows(response.data)
      if (selectedCategoryId === 'all') {
        setAllCategoryRows(response.data)
      }
      return
    }

    setCategoryRows(response.data ? [response.data] : [])
  }, [companyUrl, toast])

  const loadFoodTemplateRows = useCallback(async () => {
    if (!companyUrl) return
    setLoadingFoodTemplates(true)
    const response = await apiService.fetchFoodTemplates(companyUrl, {
      orderByOn: 'name',
      orderBy: 'ASC',
      fromIndex: foodTemplatePagination.pageIndex * foodTemplatePagination.pageSize,
      limit: foodTemplatePagination.pageSize,
    })
    setLoadingFoodTemplates(false)

    if (!response.success) {
      toast({ title: 'Unable to load food templates', description: response.error, variant: 'destructive' })
      return
    }

    setFoodTemplateRows(Array.isArray(response.data?.rows) ? response.data.rows : [])
    setFoodTemplateTotalCount(Number(response.data?.count) || 0)
  }, [companyUrl, foodTemplatePagination.pageIndex, foodTemplatePagination.pageSize, toast])

  const loadMealRows = useCallback(async () => {
    if (!companyUrl) return
    setLoadingMealRows(true)
    const response = await apiService.fetchMealTemplates(companyUrl, {
      orderByOn: 'name',
      orderBy: 'ASC',
      fromIndex: mealPagination.pageIndex * mealPagination.pageSize,
      searchtext: mealSearch.trim(),
      limit: mealPagination.pageSize,
    })
    setLoadingMealRows(false)

    if (!response.success) {
      toast({ title: 'Unable to load meal templates', description: response.error, variant: 'destructive' })
      return
    }

    setMealRows(Array.isArray(response.data?.rows) ? response.data.rows : [])
    setMealTotalCount(Number(response.data?.count) || 0)
    setSelectedMealIds([])
  }, [companyUrl, mealPagination.pageIndex, mealPagination.pageSize, mealSearch, toast])

  useEffect(() => {
    loadMealCategories()
    loadMealCategoryRows()
  }, [loadMealCategories, loadMealCategoryRows])

  const handleMealCategoryFilterChange = async (value) => {
    setCategoryFilter(value)
    await loadMealCategoryRows(value)
  }

  useEffect(() => {
    loadFoodTemplateRows()
  }, [loadFoodTemplateRows])

  useEffect(() => {
    loadMealRows()
  }, [loadMealRows])

  useEffect(() => {
    if (!nutritionSearch.trim()) {
      setNutritionResults([])
      setNutritionTotalCount(0)
      setExpandedNutritionResultId(null)
      return
    }

    const timer = window.setTimeout(async () => {
      setLoadingNutrition(true)
      const response = await apiService.searchNewNutrition(companyUrl, nutritionSearch.trim())
      setLoadingNutrition(false)

      if (!response.success) {
        toast({ title: 'Unable to search foods', description: response.error, variant: 'destructive' })
        return
      }

      const rawRows = Array.isArray(response.data?.results)
        ? response.data.results.map(buildNutritionSearchDraft)
        : []

      const uniqueRows = Array.from(new Map(rawRows.map((row) => [row.id, row])).values())

      setNutritionPagination({ ...EMPTY_SEARCH_PAGINATION })
      setNutritionResults(uniqueRows)
      setNutritionTotalCount(Number(response.data?.total) || uniqueRows.length)
    }, 300)

    return () => window.clearTimeout(timer)
  }, [companyUrl, nutritionSearch, toast])

  useEffect(() => {
    const hasActiveFood = (createForm.mealFoods || []).some((food) => food.id === createActiveFoodId)
    if (!hasActiveFood && createForm.mealFoods?.length) {
      setCreateActiveFoodId(createForm.mealFoods[0].id)
    }
  }, [createActiveFoodId, createForm.mealFoods])

  useEffect(() => {
    const hasActiveFood = (editForm.mealFoods || []).some((food) => food.id === editActiveFoodId)
    if (!hasActiveFood && editForm.mealFoods?.length) {
      setEditActiveFoodId(editForm.mealFoods[0].id)
    }
  }, [editActiveFoodId, editForm.mealFoods])

  const updateCreateField = (key, value) => {
    setCreateForm((current) => {
      const next = { ...current, [key]: value }

      if (key === 'mealtemplatecategory_id' && value !== '') {
        next.newCategoryParentId = ''
        next.newCategoryName = ''
      } else if (key === 'newCategoryParentId' && value !== '') {
        next.mealtemplatecategory_id = ''
      } else if (key === 'newCategoryName' && value !== '') {
        next.mealtemplatecategory_id = ''
      }

      return next
    })
  }

  const updateEditField = (key, value) => {
    setEditForm((current) => ({ ...current, [key]: value }))
  }

  const updateMealFoods = (target, updater) => {
    if (target === 'create') {
      setCreateForm((current) => ({
        ...current,
        mealFoods: updater(current.mealFoods || []),
      }))
      return
    }

    setEditForm((current) => ({
      ...current,
      mealFoods: updater(current.mealFoods || []),
    }))
  }

  const addFoodToDraft = (target, item, sourceType) => {
    const nextFood = buildMealFoodDraft(item, sourceType)
    updateMealFoods(target, (foods) => [...foods, nextFood])
    if (target === 'create') {
      setCreateActiveFoodId(nextFood.id)
    } else {
      setEditActiveFoodId(nextFood.id)
    }
  }

  const addNutritionMeasureToDraft = (target, detail, measure) => {
    const nextFood = buildMealFoodFromNutritionMeasure(detail, measure)
    updateMealFoods(target, (foods) => [...foods, nextFood])
    if (target === 'create') {
      setCreateActiveFoodId(nextFood.id)
    } else {
      setEditActiveFoodId(nextFood.id)
    }
  }

  const updateFoodQty = (target, foodId, value) => {
    const nextQty = value === '' ? '' : Math.max(parseNumberField(value), 0)
    updateMealFoods(target, (foods) =>
      foods.map((food) => (food.id === foodId ? { ...food, qty: nextQty } : food))
    )
  }

  const deleteDraftFood = (target, foodId) => {
    updateMealFoods(target, (foods) => foods.filter((food) => food.id !== foodId))
    setSelectedEditFoodIds((current) => current.filter((id) => id !== foodId))
    if (expandedMealFoodId === foodId) {
      setExpandedMealFoodId(null)
    }
    if (target === 'create' && createActiveFoodId === foodId) {
      setCreateActiveFoodId(null)
    }
    if (target === 'edit' && editActiveFoodId === foodId) {
      setEditActiveFoodId(null)
    }
  }

  const updateFoodField = (target, foodId, key, value) => {
    updateMealFoods(target, (foods) =>
      foods.map((food) =>
        food.id === foodId
          ? {
              ...food,
              [key]:
                ['qty', 'baseCalories', 'baseProtein', 'baseCarbs', 'baseFat'].includes(key)
                  ? value === '' ? '' : parseNumberField(value)
                  : value,
            }
          : food
      )
    )
  }

  const resetNutritionSection = () => {
    setNutritionSearch('')
    setNutritionPagination({ ...EMPTY_SEARCH_PAGINATION })
    setNutritionResults([])
    setNutritionTotalCount(0)
    setExpandedNutritionResultId(null)
  }

  const openDeleteModal = (type, payload, title, description) => {
    setDeleteModal({
      open: true,
      type,
      payload,
      title,
      description,
    })
  }

  const loadExpandedMeal = async (templateId) => {
    if (!companyUrl || !templateId) return
    setExpandedMealId(templateId)
    setLoadingExpandedMeal(true)
    setSelectedEditFoodIds([])
    setExpandedMealFoodId(null)
    const response = await apiService.fetchMealTemplateFoods(companyUrl, templateId)
    setLoadingExpandedMeal(false)

    if (!response.success) {
      toast({ title: 'Unable to load meal template', description: response.error, variant: 'destructive' })
      return
    }

    const normalized = {
      ...normalizeMealTemplate(response.data),
      mealFoods: Array.isArray(response.data?.mealtemplatefoods)
        ? response.data.mealtemplatefoods.map((food) => ({
            ...buildMealFoodDraft(food, 'meal-template-food'),
            sourceId: food.id || null,
            detail: food,
          }))
        : [],
    }

    setEditForm(normalized)
    setExpandedMealBaseline(normalized)
    setEditActiveFoodId(normalized.mealFoods?.[0]?.id || null)
  }

  const clearSelectedMeal = () => {
    setExpandedMealId(null)
    setEditForm({ ...MEAL_DEFAULT_FORM })
    setExpandedMealBaseline({ ...MEAL_DEFAULT_FORM })
    setSelectedEditFoodIds([])
    setExpandedMealFoodId(null)
    setEditActiveFoodId(null)
    setEditCatalogCollapsed(false)
  }

  const handleSelectMealRow = async (row) => {
    if (!row?.id) return
    await loadExpandedMeal(row.id)
    setEditCatalogCollapsed(true)
  }

  const handleToggleNutritionResult = async (row) => {
    if (expandedNutritionResultId === row.id) {
      setExpandedNutritionResultId(null)
      return
    }

    setExpandedNutritionResultId(row.id)
    if (nutritionDetailMap[row.id]) return

    setNutritionDetailLoadingId(row.id)
    const response = await apiService.fetchNutritionWithDetails(companyUrl, row.raw || row.name)
    setNutritionDetailLoadingId(null)

    if (!response.success) {
      toast({ title: 'Unable to load nutrient detail', description: response.error, variant: 'destructive' })
      return
    }

    const detail = Array.isArray(response.data?.foods) ? response.data.foods[0] : null
    setNutritionDetailMap((current) => ({ ...current, [row.id]: detail }))
  }

  const handleToggleMealFoodDetail = async (food) => {
    if (expandedMealFoodId === food.id) {
      setExpandedMealFoodId(null)
      return
    }

    setExpandedMealFoodId(food.id)
    if (mealFoodDetailRows[food.id]) return

    if (food.sourceType === 'meal-template-food' && expandedMealId && food.sourceId) {
      setMealFoodDetailLoadingId(food.id)
      const response = await apiService.fetchMealTemplateFoods(companyUrl, expandedMealId, food.sourceId)
      setMealFoodDetailLoadingId(null)

      if (!response.success) {
        toast({ title: 'Unable to load meal food detail', description: response.error, variant: 'destructive' })
        return
      }

      const detail = Array.isArray(response.data?.mealtemplatefoods) ? response.data.mealtemplatefoods[0] : null
      setMealFoodDetailRows((current) => ({
        ...current,
        [food.id]: detail
          ? Object.entries(detail)
              .filter(([, value]) => value !== null && value !== undefined && value !== '')
              .map(([key, value]) => ({
                key,
                label: key.replaceAll('_', ' '),
                value,
              }))
          : [],
      }))
      return
    }

    if (food.detailRows?.length) {
      setMealFoodDetailRows((current) => ({
        ...current,
        [food.id]: food.detailRows,
      }))
      return
    }

    const localDetail = food.detail || null
    setMealFoodDetailRows((current) => ({
      ...current,
      [food.id]: localDetail
        ? Object.entries(localDetail)
            .filter(([, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => ({
              key,
              label: key.replaceAll('_', ' '),
              value,
            }))
        : [],
    }))
  }

  const handleDeleteSelectedMeals = () => {
    if (!selectedMealIds.length) return
    openDeleteModal(
      'meals',
      { mealIds: [...selectedMealIds] },
      'Delete selected meal templates?',
      `This will permanently delete ${selectedMealIds.length} meal template${selectedMealIds.length === 1 ? '' : 's'}.`
    )
  }

  const handleDeleteSelectedEditFoods = () => {
    if (!selectedEditFoodIds.length) return
    openDeleteModal(
      'meal-foods',
      { mealId: expandedMealId, foodIds: [...selectedEditFoodIds] },
      'Delete selected meal foods?',
      `This will permanently delete ${selectedEditFoodIds.length} food row${selectedEditFoodIds.length === 1 ? '' : 's'} from the selected meal template.`
    )
  }

  const handleDeleteSingleEditFood = (foodId) => {
    if (!expandedMealId || !foodId) return
    openDeleteModal(
      'meal-foods',
      { mealId: expandedMealId, foodIds: [foodId] },
      'Delete this meal food?',
      'This will permanently delete the selected food row from the meal template.'
    )
  }

  const handleCreateDraftSubmit = async () => {
    if (!createForm.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' })
      return
    }

    const hasMealCategory = Boolean(createForm.mealtemplatecategory_id)
    const hasParentCategory = Boolean(createForm.newCategoryParentId)
    const hasNewCategoryName = Boolean(createForm.newCategoryName.trim())

    if (hasMealCategory && (hasParentCategory || hasNewCategoryName)) {
      toast({
        title: 'Use either Meal Category or New Meal Category',
        variant: 'destructive',
      })
      return
    }

    if (!hasMealCategory && !hasNewCategoryName) {
      toast({
        title: 'Select Meal Category or enter New Meal Category before submitting',
        variant: 'destructive',
      })
      return
    }

    if (hasParentCategory && !hasNewCategoryName) {
      toast({ title: 'New Meal Category is required', variant: 'destructive' })
      return
    }

    setSavingDraft(true)
    const payload = buildMealCreatePayload(createForm)

    const response = await apiService.createMealTemplate(companyUrl, payload)
    setSavingDraft(false)

    if (response.success) {
      toast({ title: 'Meal template created successfully' })
      setCreateForm({ ...MEAL_DEFAULT_FORM })
      loadMealCategories()
      loadMealCategoryRows()
      loadMealRows()
      onTabChange('edit')
    } else {
      toast({ title: 'Failed to create meal template', description: response.error, variant: 'destructive' })
    }
  }

  const handleSaveEditDraft = async () => {
    if (!expandedMealId) return
    if (!editForm.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' })
      return
    }

    setSavingDraft(true)
    try {
      const response = await apiService.updateMealTemplate(
        companyUrl,
        expandedMealId,
        buildMealUpdatePayload(editForm)
      )

      if (!response.success) {
        toast({ title: 'Unable to update meal template', description: response.error, variant: 'destructive' })
        return
      }

      toast({ title: 'Meal template updated' })
      await loadMealRows()
      await loadExpandedMeal(expandedMealId)
    } finally {
      setSavingDraft(false)
    }
  }

  const resetCreateDraft = () => {
    setCreateForm({ ...MEAL_DEFAULT_FORM })
    setCreateActiveFoodId(null)
    resetNutritionSection()
  }

  const resetEditDraft = () => {
    setEditForm(expandedMealBaseline)
    setEditActiveFoodId(expandedMealBaseline.mealFoods?.[0]?.id || null)
    setSelectedEditFoodIds([])
    setExpandedMealFoodId(null)
    resetNutritionSection()
  }

  const handleCategoryParentChange = async (categoryId, value) => {
    const nextParentId = value ? Number(value) : null
    const response = await apiService.updateMealTemplateCategory(companyUrl, categoryId, {
      parent_id: nextParentId,
    })

    if (response.success) {
      toast({ title: 'Category layout updated' })
      loadMealCategories()
      loadMealCategoryRows(categoryFilter)
    } else {
      toast({ title: 'Failed to update category', description: response.error, variant: 'destructive' })
    }
  }

  const handleDeleteCategory = (row) => {
    const mealIds = (row?.mealtemplates || []).map((meal) => meal.id).filter(Boolean)
    if (!mealIds.length) {
      toast({
        title: 'No meals available to delete',
        description: 'The provided contract only included a meal-template delete endpoint, so empty categories cannot be removed from this screen.',
        variant: 'destructive',
      })
      return
    }

    openDeleteModal(
      'category-meals',
      { mealIds, categoryName: row.name },
      `Delete meals under ${row.name}?`,
      `This will permanently delete ${mealIds.length} meal template${mealIds.length === 1 ? '' : 's'} currently listed under ${row.name}.`
    )
  }

  const handleAddFoodTemplateToEdit = async (row) => {
    if (!expandedMealId) {
      toast({ title: 'Select a meal template first', variant: 'destructive' })
      return
    }

    setSavingDraft(true)
    try {
      const response = await apiService.addFoodToMealTemplate(companyUrl, expandedMealId, {
        foods: [buildMealTemplateAddFoodPayloadFromTemplate(row)],
      })

      if (!response.success) {
        toast({ title: 'Unable to add food template', description: response.error, variant: 'destructive' })
        return
      }

      toast({ title: 'Food added to meal template' })
      await Promise.all([loadMealRows(), loadExpandedMeal(expandedMealId)])
    } finally {
      setSavingDraft(false)
    }
  }

  const handleAddNutritionFoodToEdit = async (detail, measure) => {
    if (!expandedMealId) {
      toast({ title: 'Select a meal template first', variant: 'destructive' })
      return
    }

    const measures = getNutritionMeasureOptions(detail)
    const selectedIndex = Math.max(
      0,
      measures.findIndex(
        (candidate) =>
          `${candidate.qty}-${candidate.measure}`.toLowerCase() ===
          `${measure?.qty}-${measure?.measure}`.toLowerCase()
      )
    )

    setSavingDraft(true)
    try {
      const response = await apiService.addFoodToMealTemplate(companyUrl, expandedMealId, {
        foods: [buildMealTemplateAddFoodPayloadFromNutrition(detail, measure, selectedIndex)],
      })

      if (!response.success) {
        toast({ title: 'Unable to add Nutritionix food', description: response.error, variant: 'destructive' })
        return
      }

      resetNutritionSection()
      setNutritionDetailMap({})
      toast({ title: 'Nutritionix food added to meal template' })
      await Promise.all([loadMealRows(), loadExpandedMeal(expandedMealId)])
    } finally {
      setSavingDraft(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteModal.type) return
    setDeletingRows(true)
    try {
      if (deleteModal.type === 'meals') {
        const response = await apiService.deleteMealTemplates(companyUrl, deleteModal.payload?.mealIds || [])
        if (!response.success) {
          toast({ title: 'Unable to delete meal templates', description: response.error, variant: 'destructive' })
          return
        }

        if ((deleteModal.payload?.mealIds || []).includes(expandedMealId)) {
          clearSelectedMeal()
        }
        setSelectedMealIds([])
        toast({ title: 'Selected meal templates deleted' })
        await Promise.all([loadMealRows(), loadMealCategoryRows(categoryFilter)])
      } else if (deleteModal.type === 'meal-foods') {
        const response = await apiService.deleteMealTemplateFoods(
          companyUrl,
          deleteModal.payload?.mealId,
          deleteModal.payload?.foodIds || []
        )
        if (!response.success) {
          toast({ title: 'Unable to delete meal foods', description: response.error, variant: 'destructive' })
          return
        }

        setSelectedEditFoodIds([])
        setExpandedMealFoodId(null)
        toast({ title: 'Selected meal foods deleted' })
        await Promise.all([loadMealRows(), loadExpandedMeal(deleteModal.payload?.mealId)])
      } else if (deleteModal.type === 'category-meals') {
        const response = await apiService.deleteMealTemplates(companyUrl, deleteModal.payload?.mealIds || [])
        if (!response.success) {
          toast({ title: 'Unable to delete category meals', description: response.error, variant: 'destructive' })
          return
        }

        if ((deleteModal.payload?.mealIds || []).includes(expandedMealId)) {
          clearSelectedMeal()
        }
        toast({ title: 'Category meals deleted' })
        await Promise.all([loadMealRows(), loadMealCategoryRows(categoryFilter)])
      }

      setDeleteModal({
        open: false,
        type: null,
        payload: null,
        title: '',
        description: '',
      })
    } finally {
      setDeletingRows(false)
    }
  }

  return (
    <div className="uploader-page">
      <PageHero
        title="Meal Templates"
        description="Manage meal templates with API-driven category loading, food-template lookup, Nutritionix food search, inline edit expansion, and category layout preview."
        onBack={onBack}
      />

      <WorkspaceTabs tabs={MEAL_TABS} activeTab={activeTab} onChange={onTabChange} />

      <div className="uploader-surface space-y-6">
        {activeTab === 'create' ? (
          <MealTemplateEditor
            mode="create"
            form={createForm}
            categories={categories}
            onFieldChange={updateCreateField}
            onReset={resetCreateDraft}
            onSubmit={handleCreateDraftSubmit}
            submitLabel={savingDraft ? 'Saving...' : 'Submit'}
            isSubmitting={savingDraft || loadingCategories}
            foods={createForm.mealFoods}
            activeFoodId={createActiveFoodId}
            activeFood={createActiveFood}
            onActivateFood={setCreateActiveFoodId}
            onFoodFieldChange={(key, value) => createActiveFood && updateFoodField('create', createActiveFood.id, key, value)}
            onFoodQtyChange={(foodId, value) => updateFoodQty('create', foodId, value)}
            onDeleteFood={(foodId) => deleteDraftFood('create', foodId)}
            nutritionSearchTerm={nutritionSearch}
            onNutritionSearchTermChange={(value) => {
              setNutritionPagination({ ...EMPTY_SEARCH_PAGINATION })
              setNutritionSearch(value)
            }}
            nutritionLoading={loadingNutrition}
            nutritionTotalCount={nutritionTotalCount}
            nutritionRows={visibleNutritionRows}
            nutritionPageIndex={nutritionPagination.pageIndex}
            nutritionPageSize={nutritionPagination.pageSize}
            onNutritionPageChange={(pageIndex) =>
              setNutritionPagination((current) => ({ ...current, pageIndex }))
            }
            expandedNutritionResultId={expandedNutritionResultId}
            onToggleNutritionResult={handleToggleNutritionResult}
            nutritionDetailMap={nutritionDetailMap}
            nutritionDetailLoadingId={nutritionDetailLoadingId}
            onAddNutritionFood={(detail, measure) => addNutritionMeasureToDraft('create', detail, measure)}
            foodTemplateRows={foodTemplateRows}
            foodTemplateLoading={loadingFoodTemplates}
            foodTemplatePageIndex={foodTemplatePagination.pageIndex}
            foodTemplatePageSize={foodTemplatePagination.pageSize}
            foodTemplateTotalCount={foodTemplateTotalCount}
            onFoodTemplatePageChange={(pageIndex) =>
              setFoodTemplatePagination((current) => ({ ...current, pageIndex }))
            }
            onAddFoodTemplate={(row) => addFoodToDraft('create', row, 'food-template')}
          />
        ) : null}

        {activeTab === 'edit' ? (
          <div className={cn('grid gap-6 transition-all duration-300', expandedMealId && editCatalogCollapsed ? 'xl:grid-cols-[88px,minmax(0,1fr)]' : 'grid-cols-1')}>
          {!editCatalogCollapsed || !expandedMealId ? (
            <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <CardTitle className="text-lg">Meal Catalog</CardTitle>
                    <CardDescription>Browse meal templates, choose one, and open its full editor.</CardDescription>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                    <div className="relative sm:min-w-[240px]">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={mealSearch}
                        onChange={(event) => {
                          setMealPagination((current) => ({ ...current, pageIndex: 0 }))
                          setMealSearch(event.target.value)
                        }}
                        placeholder="Search"
                        className="w-full pl-9"
                      />
                    </div>

                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleDeleteSelectedMeals}
                      disabled={!selectedMealIds.length}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Selected Meal(s)
                    </Button>
                    {expandedMealId ? <Button type="button" variant="outline" onClick={() => setEditCatalogCollapsed(true)}>Resume Editor</Button> : null}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {loadingMealRows ? (
                  <EmptyState message="Loading meal templates..." />
                ) : mealRows.length ? (
                  <>
                    <div className="overflow-x-auto rounded-2xl border border-slate-200">
                      <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={mealRows.length > 0 && mealRows.every((row) => selectedMealIds.includes(row.id))}
                                onChange={(event) =>
                                  setSelectedMealIds(event.target.checked ? mealRows.map((row) => row.id) : [])
                                }
                              />
                            </th>
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Category</th>
                            <th className="px-4 py-3">Description</th>
                            <th className="px-4 py-3">Note</th>
                            <th className="px-4 py-3">Video URL</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {mealRows.map((row) => (
                            <tr
                              key={row.id}
                              className={cn(
                                'cursor-pointer transition hover:bg-slate-50',
                                Number(expandedMealId) === Number(row.id) && 'bg-fitness-primary/5'
                              )}
                              onClick={() => handleSelectMealRow(row)}
                            >
                              <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedMealIds.includes(row.id)}
                                  onChange={() =>
                                    setSelectedMealIds((current) =>
                                      current.includes(row.id)
                                        ? current.filter((id) => id !== row.id)
                                        : [...current, row.id]
                                    )
                                  }
                                />
                              </td>
                              <td className="px-4 py-3 font-medium text-slate-900">{row.name}</td>
                              <td className="px-4 py-3 text-slate-600">{row.mealtemplatecategory?.name || 'Uncategorized'}</td>
                              <td className="px-4 py-3 text-slate-600">{truncateText(row.description || '', 80)}</td>
                              <td className="px-4 py-3 text-slate-600">{truncateText(row.note || '', 60)}</td>
                              <td className="px-4 py-3 text-slate-600">
                                {row.video_url ? (
                                  <a
                                    href={row.video_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-fitness-primary hover:underline"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    {truncateText(row.video_url, 60)}
                                  </a>
                                ) : (
                                  'None'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <PaginationFooter
                      pageIndex={mealPagination.pageIndex}
                      pageSize={mealPagination.pageSize}
                      totalCount={mealTotalCount}
                      onPageChange={(pageIndex) => setMealPagination((current) => ({ ...current, pageIndex }))}
                      pageSizeLabel={`${mealPagination.pageSize} / page`}
                    />
                  </>
                ) : (
                  <EmptyState message="No meal templates matched the current search." />
                )}
              </CardContent>
            </Card>
            </div>
          ) : (
            <div className="space-y-6">
              <CollapsedCatalogCard label={editForm?.name} onExpand={() => setEditCatalogCollapsed(false)} title="Meal" />
            </div>
          )}

          {expandedMealId && editCatalogCollapsed ? (
          <div className="min-w-0 space-y-6">
            {loadingExpandedMeal ? (
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="py-12">
                    <EmptyState message="Loading meal template detail..." />
                  </CardContent>
                </Card>
              ) : (
                <>

                  <MealTemplateEditor
                    mode="edit"
                    form={editForm}
                    categories={categories}
                    onFieldChange={updateEditField}
                    onReset={resetEditDraft}
                    onSubmit={handleSaveEditDraft}
                    submitLabel={savingDraft ? 'Saving...' : 'Save Changes'}
                    isSubmitting={savingDraft}
                    foods={editForm.mealFoods}
                    selectedFoodIds={selectedEditFoodIds}
                    onSelectFood={(foodId) =>
                      setSelectedEditFoodIds((current) =>
                        current.includes(foodId) ? current.filter((id) => id !== foodId) : [...current, foodId]
                      )
                    }
                    onSelectAllFoods={(checked) =>
                      setSelectedEditFoodIds(checked ? (editForm.mealFoods || []).map((food) => food.id) : [])
                    }
                    activeFoodId={editActiveFoodId}
                    activeFood={editActiveFood}
                    onActivateFood={setEditActiveFoodId}
                    onFoodFieldChange={(key, value) => editActiveFood && updateFoodField('edit', editActiveFood.id, key, value)}
                    onFoodQtyChange={(foodId, value) => updateFoodQty('edit', foodId, value)}
                    onDeleteFood={handleDeleteSingleEditFood}
                    onToggleMealFoodDetail={handleToggleMealFoodDetail}
                    expandedMealFoodId={expandedMealFoodId}
                    mealFoodDetailRows={mealFoodDetailRows}
                    mealFoodDetailLoadingId={mealFoodDetailLoadingId}
                    onDeleteSelectedFoods={handleDeleteSelectedEditFoods}
                    nutritionSearchTerm={nutritionSearch}
                    onNutritionSearchTermChange={(value) => {
                      setNutritionPagination({ ...EMPTY_SEARCH_PAGINATION })
                      setNutritionSearch(value)
                    }}
                    nutritionLoading={loadingNutrition}
                    nutritionTotalCount={nutritionTotalCount}
                    nutritionRows={visibleNutritionRows}
                    nutritionPageIndex={nutritionPagination.pageIndex}
                    nutritionPageSize={nutritionPagination.pageSize}
                    onNutritionPageChange={(pageIndex) =>
                      setNutritionPagination((current) => ({ ...current, pageIndex }))
                    }
                    expandedNutritionResultId={expandedNutritionResultId}
                    onToggleNutritionResult={handleToggleNutritionResult}
                    nutritionDetailMap={nutritionDetailMap}
                    nutritionDetailLoadingId={nutritionDetailLoadingId}
                    onAddNutritionFood={handleAddNutritionFoodToEdit}
                    foodTemplateRows={foodTemplateRows}
                    foodTemplateLoading={loadingFoodTemplates}
                    foodTemplatePageIndex={foodTemplatePagination.pageIndex}
                    foodTemplatePageSize={foodTemplatePagination.pageSize}
                    foodTemplateTotalCount={foodTemplateTotalCount}
                    onFoodTemplatePageChange={(pageIndex) =>
                      setFoodTemplatePagination((current) => ({ ...current, pageIndex }))
                    }
                    onAddFoodTemplate={handleAddFoodTemplateToEdit}
                  />
                </>
              )}
          </div>
          ) : null}
          </div>
        ) : null}

        {activeTab === 'categories' ? (
          <Card className="min-w-0 border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle className="text-lg">Meal Categories</CardTitle>
                <CardDescription>Review the category layout and the meals assigned to each category.</CardDescription>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-600">View category layout</span>
                <select
                  className="flex h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                  value={categoryFilter}
                  onChange={(event) => handleMealCategoryFilterChange(event.target.value)}
                >
                  <option value="all">All Categories</option>
                  {topLevelCategoryRows.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingCategoryRows ? (
              <EmptyState message="Loading meal categories..." />
            ) : visibleCategoryRows.length ? (
              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Parent Category</th>
                      <th className="px-5 py-3">Meals</th>
                      <th className="px-5 py-3 text-right">Delete</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {visibleCategoryRows.map((row) => (
                      <tr key={row.id}>
                        <td className="px-5 py-4 align-top font-medium text-slate-900">{row.name}</td>
                        <td className="px-5 py-4 align-top">
                          <select
                            className="flex h-10 min-w-[220px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                            value={row.parent_id || ''}
                            onChange={(event) => handleCategoryParentChange(row.id, event.target.value)}
                          >
                            <option value="">No Parent Category</option>
                            {categories
                              .filter((candidate) => candidate.id !== row.id)
                              .map((candidate) => (
                                <option key={candidate.id} value={candidate.id}>
                                  {candidate.name}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td className="px-5 py-4">
                          {row.mealtemplates?.length ? (
                            <div className="flex flex-wrap gap-2">
                              {row.mealtemplates.map((meal) => (
                                <Badge key={meal.id} className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                                  {meal.name}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-500">No meals assigned</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right align-top">
                          <Button type="button" variant="ghost" size="icon" onClick={() => handleDeleteCategory(row)}>
                            <Trash2 className="h-4 w-4 text-slate-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState message="No meal categories matched the current filter." />
            )}
          </CardContent>
          </Card>
        ) : null}
      </div>

      <ConfirmDeleteModal
        open={deleteModal.open}
        onOpenChange={(open) => setDeleteModal((prev) => ({ ...prev, open }))}
        onConfirm={confirmDelete}
        title={deleteModal.title}
        description={deleteModal.description}
        isDeleting={deletingRows}
      />
    </div>
  )
}

function RecipeTemplatesWorkspace({ companyUrl, activeTab, onTabChange, onBack }) {
  const { toast } = useToast()
  const [categories, setCategories] = useState([])
  const [allCategoryRows, setAllCategoryRows] = useState([])
  const [categoryRows, setCategoryRows] = useState([])
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [createForm, setCreateForm] = useState({ ...RECIPE_DEFAULT_FORM })
  const [createActiveFoodId, setCreateActiveFoodId] = useState(null)
  const [recipeRows, setRecipeRows] = useState([])
  const [recipeSearch, setRecipeSearch] = useState('')
  const [recipePagination, setRecipePagination] = useState({ ...EMPTY_EDIT_PAGINATION })
  const [recipeTotalCount, setRecipeTotalCount] = useState(0)
  const [selectedRecipeIds, setSelectedRecipeIds] = useState([])
  const [selectedRecipeId, setSelectedRecipeId] = useState(null)
  const [editCatalogCollapsed, setEditCatalogCollapsed] = useState(false)
  const [editForm, setEditForm] = useState({ ...RECIPE_DEFAULT_FORM })
  const [foodTemplateRows, setFoodTemplateRows] = useState([])
  const [foodTemplatePagination, setFoodTemplatePagination] = useState({ ...EMPTY_EDIT_PAGINATION })
  const [foodTemplateTotalCount, setFoodTemplateTotalCount] = useState(0)
  const [foodTemplateSearch, setFoodTemplateSearch] = useState('')
  const [nutritionSearch, setNutritionSearch] = useState('')
  const [nutritionPagination, setNutritionPagination] = useState({ ...EMPTY_SEARCH_PAGINATION })
  const [nutritionResults, setNutritionResults] = useState([])
  const [nutritionTotalCount, setNutritionTotalCount] = useState(0)
  const [expandedNutritionResultId, setExpandedNutritionResultId] = useState(null)
  const [nutritionDetailMap, setNutritionDetailMap] = useState({})
  const [copyRecipeOptions, setCopyRecipeOptions] = useState([])
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [selectedCopyRecipeId, setSelectedCopyRecipeId] = useState('')
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingCategoryRows, setLoadingCategoryRows] = useState(false)
  const [loadingRecipeRows, setLoadingRecipeRows] = useState(false)
  const [loadingFoodTemplates, setLoadingFoodTemplates] = useState(false)
  const [loadingNutrition, setLoadingNutrition] = useState(false)
  const [loadingCopyRecipes, setLoadingCopyRecipes] = useState(false)
  const [nutritionDetailLoadingId, setNutritionDetailLoadingId] = useState(null)
  const [copyingRecipe, setCopyingRecipe] = useState(false)
  const [creatingRecipe, setCreatingRecipe] = useState(false)
  const [savingRecipeEdit, setSavingRecipeEdit] = useState(false)
  const [deletingRecipes, setDeletingRecipes] = useState(false)
  const [updatingRecipeCategoryId, setUpdatingRecipeCategoryId] = useState(null)
  const [deletingRecipeCategory, setDeletingRecipeCategory] = useState(false)
  const [deleteCategoryModal, setDeleteCategoryModal] = useState({
    open: false,
    categoryId: null,
    categoryName: '',
  })

  const visibleNutritionRows = nutritionResults.slice(
    nutritionPagination.pageIndex * nutritionPagination.pageSize,
    nutritionPagination.pageIndex * nutritionPagination.pageSize + nutritionPagination.pageSize
  )
  const topLevelCategoryRows = allCategoryRows.filter((row) => !row.parent_id)
  const visibleCategoryRows = categoryRows
  const createActiveFood = (createForm.recipeFoods || []).find((food) => food.id === createActiveFoodId) || null

  const loadRecipeCategories = useCallback(async () => {
    if (!companyUrl) return
    setLoadingCategories(true)
    const response = await apiService.fetchRecipeCategories(companyUrl)
    setLoadingCategories(false)

    if (!response.success) {
      toast({ title: 'Unable to load recipe categories', description: response.error, variant: 'destructive' })
      return
    }

    setCategories(Array.isArray(response.data) ? response.data : [])
  }, [companyUrl, toast])

  const loadRecipeCategoryRows = useCallback(async (selectedCategoryId = 'all') => {
    if (!companyUrl) return
    setLoadingCategoryRows(true)
    const response = await apiService.fetchRecipesPerCategory(
      companyUrl,
      selectedCategoryId === 'all' ? null : selectedCategoryId
    )
    setLoadingCategoryRows(false)

    if (!response.success) {
      toast({ title: 'Unable to load recipe category layout', description: response.error, variant: 'destructive' })
      return
    }

    if (Array.isArray(response.data)) {
      setCategoryRows(response.data)
      if (selectedCategoryId === 'all') {
        setAllCategoryRows(response.data)
      }
      return
    }

    setCategoryRows(response.data ? [response.data] : [])
  }, [companyUrl, toast])

  const loadRecipeRows = useCallback(async () => {
    if (!companyUrl) return
    setLoadingRecipeRows(true)
    const response = await apiService.fetchRecipes(companyUrl, {
      orderByOn: 'name',
      orderBy: 'ASC',
      limit: recipePagination.pageSize,
      fromIndex: recipePagination.pageIndex * recipePagination.pageSize,
      searchtext: recipeSearch.trim(),
    })
    setLoadingRecipeRows(false)

    if (!response.success) {
      toast({ title: 'Unable to load recipe templates', description: response.error, variant: 'destructive' })
      return
    }

    setRecipeRows(Array.isArray(response.data?.rows) ? response.data.rows : [])
    setRecipeTotalCount(Number(response.data?.count) || 0)
    setSelectedRecipeIds([])
  }, [companyUrl, recipePagination.pageIndex, recipePagination.pageSize, recipeSearch, toast])

  const loadCopyRecipeOptions = useCallback(async () => {
    if (!companyUrl) return
    setLoadingCopyRecipes(true)
    const response = await apiService.fetchRecipes(companyUrl, {
      orderByOn: 'name',
      orderBy: 'ASC',
      limit: 1000,
      fromIndex: 0,
      searchtext: '',
    })
    setLoadingCopyRecipes(false)

    if (!response.success) {
      toast({ title: 'Unable to load recipes to copy', description: response.error, variant: 'destructive' })
      return
    }

    setCopyRecipeOptions(Array.isArray(response.data?.rows) ? response.data.rows : [])
  }, [companyUrl, toast])

  const loadFoodTemplateRows = useCallback(async () => {
    if (!companyUrl) return
    setLoadingFoodTemplates(true)
    const response = await apiService.fetchFoodTemplates(companyUrl, {
      orderByOn: 'name',
      orderBy: 'ASC',
      fromIndex: foodTemplatePagination.pageIndex * foodTemplatePagination.pageSize,
      limit: foodTemplatePagination.pageSize,
      searchtext: foodTemplateSearch.trim(),
    })
    setLoadingFoodTemplates(false)

    if (!response.success) {
      toast({ title: 'Unable to load food templates', description: response.error, variant: 'destructive' })
      return
    }

    setFoodTemplateRows(Array.isArray(response.data?.rows) ? response.data.rows : [])
    setFoodTemplateTotalCount(Number(response.data?.count) || 0)
  }, [companyUrl, foodTemplatePagination.pageIndex, foodTemplatePagination.pageSize, foodTemplateSearch, toast])

  useEffect(() => {
    loadRecipeCategories()
    loadRecipeCategoryRows()
    loadRecipeRows()
    loadFoodTemplateRows()
  }, [loadFoodTemplateRows, loadRecipeCategories, loadRecipeCategoryRows, loadRecipeRows])

  useEffect(() => {
    if (!nutritionSearch.trim()) {
      setNutritionResults([])
      setNutritionTotalCount(0)
      setExpandedNutritionResultId(null)
      return
    }

    const timer = window.setTimeout(async () => {
      setLoadingNutrition(true)
      const response = await apiService.searchNutrition(companyUrl, nutritionSearch.trim())
      setLoadingNutrition(false)

      if (!response.success) {
        toast({ title: 'Unable to search foods', description: response.error, variant: 'destructive' })
        return
      }

      const rawRows = Array.isArray(response.data?.results)
        ? response.data.results.map(buildNutritionSearchDraft)
        : []

      const uniqueRows = Array.from(new Map(rawRows.map((row) => [row.id, row])).values())

      setNutritionPagination({ ...EMPTY_SEARCH_PAGINATION })
      setNutritionResults(uniqueRows)
      setNutritionTotalCount(Number(response.data?.total) || uniqueRows.length)
    }, 300)

    return () => window.clearTimeout(timer)
  }, [companyUrl, nutritionSearch, toast])

  const updateCreateField = (key, value) => {
    setCreateForm((current) => {
      const next = { ...current, [key]: value }

      if (key === 'recipetemplatecategory_id' && value !== '') {
        next.newCategoryParentId = ''
        next.newCategoryName = ''
      } else if (key === 'newCategoryParentId' && value !== '') {
        next.recipetemplatecategory_id = ''
      } else if (key === 'newCategoryName' && `${value}`.trim() !== '') {
        next.recipetemplatecategory_id = ''
      }

      return next
    })
  }

  const updateEditField = (key, value) => {
    setEditForm((current) => ({ ...current, [key]: value }))
  }

  const updateRecipeFoods = (updater) => {
    setCreateForm((current) => ({
      ...current,
      recipeFoods: updater(current.recipeFoods || []),
    }))
  }

  const addRecipeFoodTemplate = (row) => {
    const nextFood = buildMealFoodDraft(row, 'food-template')
    updateRecipeFoods((foods) => [...foods, nextFood])
    setCreateActiveFoodId(nextFood.id)
  }

  const addRecipeNutritionFood = (detail, measure) => {
    const nextFood = buildMealFoodFromNutritionMeasure(detail, measure)
    updateRecipeFoods((foods) => [...foods, nextFood])
    setCreateActiveFoodId(nextFood.id)
    setNutritionSearch('')
    setNutritionPagination({ ...EMPTY_SEARCH_PAGINATION })
    setNutritionResults([])
    setNutritionTotalCount(0)
    setExpandedNutritionResultId(null)
  }

  const updateRecipeFoodQty = (foodId, value) => {
    const nextQty = value === '' ? '' : Math.max(parseNumberField(value), 0)
    updateRecipeFoods((foods) =>
      foods.map((food) => (food.id === foodId ? { ...food, qty: nextQty } : food))
    )
  }

  const deleteRecipeFood = (foodId) => {
    updateRecipeFoods((foods) => foods.filter((food) => food.id !== foodId))
    if (createActiveFoodId === foodId) {
      setCreateActiveFoodId(null)
    }
  }

  const resetRecipeCreateDraft = () => {
    setCreateForm({ ...RECIPE_DEFAULT_FORM })
    setCreateActiveFoodId(null)
    setNutritionSearch('')
    setNutritionPagination({ ...EMPTY_SEARCH_PAGINATION })
    setNutritionResults([])
    setNutritionTotalCount(0)
    setExpandedNutritionResultId(null)
  }

  const openCopyRecipeDialog = async () => {
    setCopyDialogOpen(true)
    setSelectedCopyRecipeId('')
    await loadCopyRecipeOptions()
  }

  const populateRecipeCreateFormFromDetail = (recipeDetail, fallbackRecipe = null) => {
    const detail = recipeDetail?.data || recipeDetail || {}
    const primaryCategory = Array.isArray(detail?.recipecategories)
      ? detail.recipecategories[0]
      : Array.isArray(fallbackRecipe?.recipecategories)
        ? fallbackRecipe.recipecategories[0]
        : null
    const recipeFoods = Array.isArray(detail?.recipefoods)
      ? detail.recipefoods.map(buildRecipeFoodDraftFromRecipeDetail)
      : []

    setCreateForm({
      ...RECIPE_DEFAULT_FORM,
      name: detail?.name || '',
      description: detail?.description || '',
      instructions: detail?.instructions || '',
      note: detail?.note || '',
      video_url: detail?.video_url || '',
      recipetemplatecategory_id: toFormValue(primaryCategory?.id || ''),
      unit: detail?.unit || '',
      qty: toFormValue(detail?.qty || ''),
      recipeFoods,
      useCopiedRecipeImages: Array.isArray(detail?.recipeimages) && detail.recipeimages.length > 0,
    })
    setCreateActiveFoodId(recipeFoods[0]?.id || null)
    setNutritionSearch('')
    setNutritionPagination({ ...EMPTY_SEARCH_PAGINATION })
    setNutritionResults([])
    setNutritionTotalCount(0)
    setExpandedNutritionResultId(null)
  }

  const handleCopyExistingRecipe = async () => {
    if (!selectedCopyRecipeId) {
      toast({ title: 'Select a recipe to copy', variant: 'destructive' })
      return
    }

    setCopyingRecipe(true)
    try {
      const response = await apiService.fetchRecipeDetails(companyUrl, selectedCopyRecipeId)

      if (!response.success) {
        toast({ title: 'Unable to copy recipe', description: response.error, variant: 'destructive' })
        return
      }

      const selectedRecipe =
        copyRecipeOptions.find((recipe) => `${recipe.id}` === `${selectedCopyRecipeId}`) || null
      populateRecipeCreateFormFromDetail(response.data, selectedRecipe)
      setCopyDialogOpen(false)
      setSelectedCopyRecipeId('')
      toast({ title: 'Recipe copied into create form' })
    } finally {
      setCopyingRecipe(false)
    }
  }

  const handleToggleNutritionResult = async (row) => {
    if (expandedNutritionResultId === row.id) {
      setExpandedNutritionResultId(null)
      return
    }

    setExpandedNutritionResultId(row.id)
    if (nutritionDetailMap[row.id]) return

    setNutritionDetailLoadingId(row.id)
    const response = await apiService.fetchNutritionWithDetails(companyUrl, row.raw || row.name)
    setNutritionDetailLoadingId(null)

    if (!response.success) {
      toast({ title: 'Unable to load nutrient detail', description: response.error, variant: 'destructive' })
      return
    }

    const detail = Array.isArray(response.data?.foods) ? response.data.foods[0] : null
    setNutritionDetailMap((current) => ({ ...current, [row.id]: detail }))
  }

  const handleCreateRecipeDraft = async () => {
    if (!createForm.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' })
      return
    }

    if (!createForm.description.trim()) {
      toast({ title: 'Description is required', variant: 'destructive' })
      return
    }

    if (!createForm.note.trim()) {
      toast({ title: 'Note is required', variant: 'destructive' })
      return
    }

    const hasRecipeCategory = Boolean(createForm.recipetemplatecategory_id)
    const hasParentCategory = Boolean(createForm.newCategoryParentId)
    const hasNewCategoryName = Boolean(createForm.newCategoryName.trim())

    if (hasRecipeCategory && (hasParentCategory || hasNewCategoryName)) {
      toast({
        title: 'Use either Recipe Category or New Recipe Category',
        variant: 'destructive',
      })
      return
    }

    if (!hasRecipeCategory && !hasNewCategoryName) {
      toast({
        title: 'Select Recipe Category or enter New Recipe Category before submitting',
        variant: 'destructive',
      })
      return
    }

    if (!createForm.unit.trim()) {
      toast({ title: 'Recipe unit name is required', variant: 'destructive' })
      return
    }

    if (`${createForm.qty}`.trim() === '') {
      toast({ title: 'Default Qty is required', variant: 'destructive' })
      return
    }

    if (hasParentCategory && !hasNewCategoryName) {
      toast({ title: 'New Recipe Category is required', variant: 'destructive' })
      return
    }

    setCreatingRecipe(true)
    try {
      const response = await apiService.createRecipe(companyUrl, buildRecipeCreatePayload(createForm))

      if (!response.success) {
        toast({ title: 'Failed to create recipe template', description: response.error, variant: 'destructive' })
        return
      }

      toast({ title: 'Recipe template created successfully' })
      resetRecipeCreateDraft()
      loadRecipeCategories()
      loadRecipeCategoryRows()
      loadRecipeRows()
      onTabChange('edit')
    } finally {
      setCreatingRecipe(false)
    }
  }

  const handleSelectRecipeRow = (row) => {
    if (!row?.id) return
    setSelectedRecipeId(row.id)
    setEditForm(normalizeRecipeTemplate(row))
    setEditCatalogCollapsed(true)
  }

  const clearSelectedRecipe = () => {
    setSelectedRecipeId(null)
    setEditForm({ ...RECIPE_DEFAULT_FORM })
    setEditCatalogCollapsed(false)
  }

  const handleSaveRecipeEdit = async () => {
    if (!selectedRecipeId) return

    if (!editForm.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' })
      return
    }

    if (!editForm.description.trim()) {
      toast({ title: 'Description is required', variant: 'destructive' })
      return
    }

    if (!editForm.note.trim()) {
      toast({ title: 'Note is required', variant: 'destructive' })
      return
    }

    if (!editForm.recipetemplatecategory_id) {
      toast({ title: 'Recipe category is required', variant: 'destructive' })
      return
    }

    if (!editForm.unit.trim()) {
      toast({ title: 'Recipe unit name is required', variant: 'destructive' })
      return
    }

    if (`${editForm.qty}`.trim() === '') {
      toast({ title: 'Default Qty is required', variant: 'destructive' })
      return
    }

    setSavingRecipeEdit(true)
    try {
      const response = await apiService.updateRecipe(companyUrl, selectedRecipeId, buildRecipeUpdatePayload(editForm))

      if (!response.success) {
        toast({ title: 'Failed to update recipe template', description: response.error, variant: 'destructive' })
        return
      }

      toast({ title: 'Recipe template updated successfully' })
      await Promise.all([loadRecipeRows(), loadRecipeCategoryRows(categoryFilter)])
    } finally {
      setSavingRecipeEdit(false)
    }
  }

  const handleDeleteRecipes = async () => {
    if (!selectedRecipeIds.length) return

    setDeletingRecipes(true)
    try {
      const response = await apiService.deleteRecipes(companyUrl, selectedRecipeIds)

      if (!response.success) {
        toast({ title: 'Failed to delete recipe templates', description: response.error, variant: 'destructive' })
        return
      }

      if (selectedRecipeId && selectedRecipeIds.includes(selectedRecipeId)) {
        clearSelectedRecipe()
      }

      toast({ title: 'Recipe template(s) deleted successfully' })
      setSelectedRecipeIds([])
      await Promise.all([loadRecipeRows(), loadRecipeCategoryRows(categoryFilter)])
    } finally {
      setDeletingRecipes(false)
    }
  }

  const handleRecipeCategoryFilterChange = async (value) => {
    setCategoryFilter(value)
    await loadRecipeCategoryRows(value)
  }

  const handleRecipeCategoryParentChange = async (categoryId, value) => {
    const parentName = value ? getCategoryName(topLevelCategoryRows, value) : 'No Parent Category'

    setUpdatingRecipeCategoryId(categoryId)
    try {
      const response = await apiService.updateRecipeCategory(companyUrl, categoryId, {
        parent_id: value ? Number(value) : null,
      })

      if (!response.success) {
        toast({ title: 'Failed to update recipe category', description: response.error, variant: 'destructive' })
        return
      }

      setCategoryRows((current) =>
        current.map((row) =>
          row.id === categoryId
            ? {
                ...row,
                parent_id: value ? Number(value) : null,
                parentcategory: value ? { id: Number(value), name: parentName } : null,
              }
            : row
        )
      )
      setAllCategoryRows((current) =>
        current.map((row) =>
          row.id === categoryId
            ? {
                ...row,
                parent_id: value ? Number(value) : null,
                parentcategory: value ? { id: Number(value), name: parentName } : null,
              }
            : row
        )
      )
      await loadRecipeCategories()
      toast({ title: 'Recipe category updated successfully' })
    } finally {
      setUpdatingRecipeCategoryId(null)
    }
  }

  const handleDeleteRecipeCategory = (categoryId) => {
    const category = categoryRows.find((row) => row.id === categoryId)
    setDeleteCategoryModal({
      open: true,
      categoryId,
      categoryName: category?.name || 'this category',
    })
  }

  const performDeleteRecipeCategory = async () => {
    if (!deleteCategoryModal.categoryId) return

    setDeletingRecipeCategory(true)
    try {
      const response = await apiService.deleteRecipeCategory(companyUrl, deleteCategoryModal.categoryId)

      if (!response.success) {
        toast({ title: 'Failed to delete recipe category', description: response.error, variant: 'destructive' })
        return
      }

      const nextFilter =
        `${categoryFilter}` === `${deleteCategoryModal.categoryId}` ? 'all' : categoryFilter

      if (nextFilter !== categoryFilter) {
        setCategoryFilter(nextFilter)
      }

      setAllCategoryRows((current) => current.filter((row) => row.id !== deleteCategoryModal.categoryId))
      setDeleteCategoryModal({ open: false, categoryId: null, categoryName: '' })
      toast({ title: 'Recipe category deleted successfully' })
      await Promise.all([loadRecipeCategories(), loadRecipeCategoryRows(nextFilter)])
    } finally {
      setDeletingRecipeCategory(false)
    }
  }

  return (
    <div className="uploader-page">
      <PageHero
        title="Recipe Templates"
        description="Manage recipe templates with category lookups, ingredient libraries, and category layout previews."
        onBack={onBack}
      />

      <WorkspaceTabs tabs={RECIPE_TABS} activeTab={activeTab} onChange={onTabChange} />

      <Dialog
        open={copyDialogOpen}
        onOpenChange={(open) => {
          setCopyDialogOpen(open)
          if (!open) {
            setSelectedCopyRecipeId('')
          }
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Copy Existing Recipe</DialogTitle>
            <DialogDescription>Select a saved recipe to prefill the create form.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Select Recipe</label>
            <select
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
              value={selectedCopyRecipeId}
              onChange={(event) => setSelectedCopyRecipeId(event.target.value)}
              disabled={loadingCopyRecipes || copyingRecipe}
            >
              <option value="">Select Recipe</option>
              {copyRecipeOptions.map((recipe) => (
                <option key={recipe.id} value={recipe.id}>
                  {recipe.name}
                </option>
              ))}
            </select>
          </div>

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCopyDialogOpen(false)
                setSelectedCopyRecipeId('')
              }}
              disabled={copyingRecipe}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleCopyExistingRecipe}
              disabled={loadingCopyRecipes || copyingRecipe}
            >
              {copyingRecipe ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="uploader-surface space-y-6">
        {activeTab === 'create' ? (
          <RecipeTemplateEditor
            form={createForm}
            categories={categories}
            foods={createForm.recipeFoods}
            onFieldChange={updateCreateField}
            onImageFilesChange={(event) => updateCreateField('imageFiles', Array.from(event.target.files || []))}
            onReset={resetRecipeCreateDraft}
            onSubmit={handleCreateRecipeDraft}
            submitLabel="Submit"
            isSubmitting={creatingRecipe || loadingCategories}
            activeFoodId={createActiveFoodId}
            onActivateFood={setCreateActiveFoodId}
            onFoodQtyChange={updateRecipeFoodQty}
            onDeleteFood={deleteRecipeFood}
            nutritionSearchTerm={nutritionSearch}
            onNutritionSearchTermChange={(value) => {
              setNutritionPagination({ ...EMPTY_SEARCH_PAGINATION })
              setNutritionSearch(value)
            }}
            nutritionLoading={loadingNutrition}
            nutritionTotalCount={nutritionTotalCount}
            nutritionRows={visibleNutritionRows}
            nutritionPageIndex={nutritionPagination.pageIndex}
            nutritionPageSize={nutritionPagination.pageSize}
            onNutritionPageChange={(pageIndex) => setNutritionPagination((current) => ({ ...current, pageIndex }))}
            expandedNutritionResultId={expandedNutritionResultId}
            onToggleNutritionResult={handleToggleNutritionResult}
            nutritionDetailMap={nutritionDetailMap}
            nutritionDetailLoadingId={nutritionDetailLoadingId}
            onAddNutritionFood={addRecipeNutritionFood}
            foodTemplateRows={foodTemplateRows}
            foodTemplateLoading={loadingFoodTemplates}
            foodTemplatePageIndex={foodTemplatePagination.pageIndex}
            foodTemplatePageSize={foodTemplatePagination.pageSize}
            foodTemplateTotalCount={foodTemplateTotalCount}
            onFoodTemplatePageChange={(pageIndex) =>
              setFoodTemplatePagination((current) => ({ ...current, pageIndex }))
            }
            onAddFoodTemplate={addRecipeFoodTemplate}
            foodTemplateSearchTerm={foodTemplateSearch}
            onFoodTemplateSearchTermChange={(value) => {
              setFoodTemplatePagination((current) => ({ ...current, pageIndex: 0 }))
              setFoodTemplateSearch(value)
            }}
            onCopyExistingRecipe={openCopyRecipeDialog}
          />
        ) : null}

        {activeTab === 'edit' ? (
          <div className={cn('grid gap-6', selectedRecipeId && editCatalogCollapsed ? 'xl:grid-cols-[88px,minmax(0,1fr)]' : 'grid-cols-1')}>
          {selectedRecipeId && editCatalogCollapsed ? (
          <div className="space-y-6">
            <CollapsedCatalogCard label={editForm?.name} onExpand={() => setEditCatalogCollapsed(false)} title="Recipe" />
          </div>
          ) : (
          <div className="space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <CardTitle className="text-lg">Recipe Catalog</CardTitle>
                  <CardDescription>Browse recipe templates and open the selected draft editor.</CardDescription>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                  <div className="relative sm:min-w-[240px]">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={recipeSearch}
                      onChange={(event) => {
                        setRecipePagination((current) => ({ ...current, pageIndex: 0 }))
                        setRecipeSearch(event.target.value)
                      }}
                      placeholder="Search"
                      className="w-full pl-9"
                    />
                  </div>

                  <Button
                    type="button"
                    variant="destructive"
                    disabled={!selectedRecipeIds.length || deletingRecipes}
                    onClick={handleDeleteRecipes}
                  >
                    {deletingRecipes ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                    Delete Selected
                  </Button>
                  {selectedRecipeId ? <Button type="button" variant="outline" onClick={() => setEditCatalogCollapsed(true)}>Resume Editor</Button> : null}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingRecipeRows ? (
                <EmptyState message="Loading recipe templates..." />
              ) : recipeRows.length ? (
                <>
                  <div className="overflow-x-auto rounded-2xl border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={recipeRows.length > 0 && recipeRows.every((row) => selectedRecipeIds.includes(row.id))}
                              onChange={(event) =>
                                setSelectedRecipeIds(event.target.checked ? recipeRows.map((row) => row.id) : [])
                              }
                            />
                          </th>
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Category</th>
                          <th className="px-4 py-3">Description</th>
                          <th className="px-4 py-3">Instructions</th>
                          <th className="px-4 py-3">Note</th>
                          <th className="px-4 py-3">Video URL</th>
                          <th className="px-4 py-3">Recipe Unit</th>
                          <th className="px-4 py-3">Qty</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {recipeRows.map((row) => {
                          const rowCategory = Array.isArray(row.recipecategories) ? row.recipecategories[0]?.name : ''

                          return (
                            <tr
                              key={row.id}
                              className={cn(
                                'cursor-pointer transition hover:bg-slate-50',
                                Number(selectedRecipeId) === Number(row.id) && 'bg-fitness-primary/5'
                              )}
                              onClick={() => handleSelectRecipeRow(row)}
                            >
                              <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedRecipeIds.includes(row.id)}
                                  onChange={() =>
                                    setSelectedRecipeIds((current) =>
                                      current.includes(row.id)
                                        ? current.filter((id) => id !== row.id)
                                        : [...current, row.id]
                                    )
                                  }
                                />
                              </td>
                              <td className="px-4 py-3 font-medium text-slate-900">{row.name}</td>
                              <td className="px-4 py-3 text-slate-600">{rowCategory || 'Uncategorized'}</td>
                              <td className="px-4 py-3 text-slate-600">{truncateText(row.description || '', 80)}</td>
                              <td className="px-4 py-3 text-slate-600">{truncateText(row.instructions || '', 120)}</td>
                              <td className="px-4 py-3 text-slate-600">{truncateText(row.note || '', 70)}</td>
                              <td className="px-4 py-3 text-slate-600">{row.video_url || 'None'}</td>
                              <td className="px-4 py-3 text-slate-600">{row.unit || 'N/A'}</td>
                              <td className="px-4 py-3 text-slate-600">{formatNumberSmart(row.qty)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  <PaginationFooter
                    pageIndex={recipePagination.pageIndex}
                    pageSize={recipePagination.pageSize}
                    totalCount={recipeTotalCount}
                    onPageChange={(pageIndex) => setRecipePagination((current) => ({ ...current, pageIndex }))}
                    pageSizeLabel={`${recipePagination.pageSize} / page`}
                  />
                </>
              ) : (
                <EmptyState message="No recipe templates matched the current search." />
              )}
            </CardContent>
          </Card>
          </div>
          )}

          {selectedRecipeId && editCatalogCollapsed ? (
          <div className="min-w-0 space-y-6">
              <>

                <RecipeTemplateDetailForm
                  form={editForm}
                  categories={categories}
                  onFieldChange={updateEditField}
                  onSubmit={handleSaveRecipeEdit}
                  onCancel={clearSelectedRecipe}
                  submitLabel="Save Changes"
                  isSubmitting={savingRecipeEdit}
                />
              </>
          </div>
          ) : null}
          </div>
        ) : null}

        {activeTab === 'categories' ? (
          <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle className="text-lg">Recipe Categories</CardTitle>
                <CardDescription>Review the category layout and the recipes assigned to each category.</CardDescription>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-600">View category layout</span>
                <select
                  className="flex h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                  value={categoryFilter}
                  onChange={(event) => handleRecipeCategoryFilterChange(event.target.value)}
                >
                  <option value="all">All Categories</option>
                  {topLevelCategoryRows.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingCategoryRows ? (
              <EmptyState message="Loading recipe categories..." />
            ) : visibleCategoryRows.length ? (
              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Parent Category</th>
                      <th className="px-5 py-3">Recipes</th>
                      <th className="px-5 py-3 text-right">Delete</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {visibleCategoryRows.map((row) => (
                      <tr key={row.id}>
                        <td className="px-5 py-4 align-top font-medium text-slate-900">{row.name}</td>
                        <td className="px-5 py-4 align-top">
                          <select
                            className="flex h-10 min-w-[220px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fitness-primary focus:outline-none"
                            value={row.parent_id || ''}
                            onChange={(event) => handleRecipeCategoryParentChange(row.id, event.target.value)}
                            disabled={updatingRecipeCategoryId === row.id}
                          >
                            <option value="">No Parent Category</option>
                            {topLevelCategoryRows
                              .filter((candidate) => candidate.id !== row.id)
                              .map((candidate) => (
                                <option key={candidate.id} value={candidate.id}>
                                  {candidate.name}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td className="px-5 py-4 align-top">
                          {row.recipes?.length ? (
                            <div className="flex max-h-[116px] min-h-[116px] flex-wrap content-start gap-2 overflow-y-auto pr-2 custom-scrollbar">
                              {row.recipes.map((recipe) => (
                                <Badge key={recipe.id} className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                                  {recipe.name}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <div className="flex min-h-[116px] items-start py-1">
                              <span className="text-slate-500">No recipes assigned</span>
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right align-top">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteRecipeCategory(row.id)}
                            disabled={deletingRecipeCategory && deleteCategoryModal.categoryId === row.id}
                          >
                            <Trash2 className="h-4 w-4 text-slate-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState message="No recipe categories matched the current filter." />
            )}
          </CardContent>
          </Card>
        ) : null}
      </div>

      <ConfirmDeleteModal
        open={deleteCategoryModal.open}
        onOpenChange={(open) =>
          setDeleteCategoryModal((current) =>
            open ? current : { open: false, categoryId: null, categoryName: '' }
          )
        }
        onConfirm={performDeleteRecipeCategory}
        title="Delete Recipe Category"
        description={`Are you sure you want to delete ${deleteCategoryModal.categoryName}? This action cannot be undone.`}
        isDeleting={deletingRecipeCategory}
      />
    </div>
  )
}

function PlaceholderWorkspace({ title, onBack }) {
  return (
    <div className="uploader-page">
      <PageHero
        title={title}
        description="This uploader workspace has not been rebuilt yet."
        onBack={onBack}
      />

      <div className="uploader-surface">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex min-h-[260px] flex-col items-center justify-center gap-4 p-8 text-center">
            <LayoutGrid className="h-10 w-10 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900">Coming soon</h3>
            <p className="max-w-md text-sm leading-6 text-slate-500">
              This uploader area will use the same workspace structure once its API mappings are added.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function Uploader() {
  const { companyUrl } = useCompanyDetails()
  const resolvedCompanyUrl = companyUrl || apiService.getCompanyUrl()
  const [searchParams, setSearchParams] = useSearchParams()
  const section = searchParams.get('section') || ''
  const foodTab = searchParams.get('foodTab') || 'create'
  const exerciseTab = searchParams.get('exerciseTab') || 'create'
  const mealTab = searchParams.get('mealTab') || 'create'
  const recipeTab = searchParams.get('recipeTab') || 'create'
  const nutritionTab = searchParams.get('nutritionTab') || 'create'
  const exercisePlanTab = searchParams.get('exercisePlanTab') || 'create'

  const updateParams = (updates) => {
    const params = new URLSearchParams(searchParams)
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        params.delete(key)
      } else {
        params.set(key, value)
      }
    })
    setSearchParams(params)
  }

  const openWorkspace = (workspaceId) => {
    if (workspaceId === 'food-templates') {
      updateParams({ section: workspaceId, foodTab: 'create', exerciseTab: null, mealTab: null, recipeTab: null, nutritionTab: null, exercisePlanTab: null })
      return
    }

    if (workspaceId === 'exercise-templates') {
      updateParams({ section: workspaceId, exerciseTab: 'create', foodTab: null, mealTab: null, recipeTab: null, nutritionTab: null, exercisePlanTab: null })
      return
    }

    if (workspaceId === 'meal-templates') {
      updateParams({ section: workspaceId, mealTab: 'create', foodTab: null, exerciseTab: null, recipeTab: null, nutritionTab: null, exercisePlanTab: null })
      return
    }

    if (workspaceId === 'recipe-templates') {
      updateParams({ section: workspaceId, recipeTab: 'create', foodTab: null, exerciseTab: null, mealTab: null, nutritionTab: null, exercisePlanTab: null })
      return
    }

    if (workspaceId === 'nutrition-programs') {
      updateParams({ section: workspaceId, nutritionTab: 'create', foodTab: null, exerciseTab: null, mealTab: null, recipeTab: null, exercisePlanTab: null })
      return
    }

    if (workspaceId === 'exercise-plans') {
      updateParams({ section: workspaceId, exercisePlanTab: 'create', foodTab: null, exerciseTab: null, mealTab: null, recipeTab: null, nutritionTab: null })
      return
    }

    if (workspaceId === 'bulk-user-upload') {
      updateParams({ section: workspaceId, foodTab: null, exerciseTab: null, mealTab: null, recipeTab: null, nutritionTab: null, exercisePlanTab: null })
      return
    }

    updateParams({ section: workspaceId, foodTab: null, exerciseTab: null, mealTab: null, recipeTab: null, nutritionTab: null, exercisePlanTab: null })
  }

  const closeWorkspace = () => {
    updateParams({ section: null, foodTab: null, exerciseTab: null, mealTab: null, recipeTab: null, nutritionTab: null, exercisePlanTab: null })
  }

  if (!section) {
    return <WorkspacePicker onSelect={openWorkspace} />
  }

  if (section === 'food-templates') {
    return (
      <FoodTemplatesWorkspace
        companyUrl={resolvedCompanyUrl}
        activeTab={foodTab}
        onTabChange={(tab) => updateParams({ section, foodTab: tab })}
        onBack={closeWorkspace}
      />
    )
  }

  if (section === 'exercise-templates') {
    return (
      <ExerciseTemplatesWorkspace
        companyUrl={resolvedCompanyUrl}
        activeTab={exerciseTab}
        onTabChange={(tab) => updateParams({ section, exerciseTab: tab })}
        onBack={closeWorkspace}
      />
    )
  }

  if (section === 'meal-templates') {
    return (
      <MealTemplatesWorkspace
        companyUrl={resolvedCompanyUrl}
        activeTab={mealTab}
        onTabChange={(tab) => updateParams({ section, mealTab: tab })}
        onBack={closeWorkspace}
      />
    )
  }

  if (section === 'recipe-templates') {
    return (
      <RecipeTemplatesWorkspace
        companyUrl={resolvedCompanyUrl}
        activeTab={recipeTab}
        onTabChange={(tab) => updateParams({ section, recipeTab: tab })}
        onBack={closeWorkspace}
      />
    )
  }

  if (section === 'nutrition-programs') {
    return (
      <NutritionProgramsWorkspace
        companyUrl={resolvedCompanyUrl}
        activeTab={nutritionTab}
        onTabChange={(tab) => updateParams({ section, nutritionTab: tab })}
        onBack={closeWorkspace}
      />
    )
  }

  if (section === 'exercise-plans') {
    return (
      <ExercisePlanTemplatesWorkspace
        companyUrl={resolvedCompanyUrl}
        activeTab={exercisePlanTab}
        onTabChange={(tab) => updateParams({ section, exercisePlanTab: tab })}
        onBack={closeWorkspace}
      />
    )
  }

  if (section === 'bulk-user-upload') {
    return <UserUpload onBack={closeWorkspace} />
  }

  const workspace = WORKSPACES.find((item) => item.id === section)
  return <PlaceholderWorkspace title={workspace?.title || 'Uploader'} onBack={closeWorkspace} />
}

export default Uploader
